import React, { useRef, useEffect, useState, useContext, useMemo, useCallback } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import apiFetch from "../../utils/apiFetch";
import { parseCoordinate } from "../../utils/coords";
import "./customMap.scss";
import UserContext from "../../hooks/UserContext"
import {
  fetchInspectionLineDetails,
  fetchInspectionLines,
  fetchInspectionPoleDetails,
  fetchInspectionPoles,
  fetchInspectionSummary,
} from "./inspection/inspectionApi";
import {
  addInspectionLayers,
  clearInspectionData,
  clearInspectionSelection,
  INSPECTION_LAYER_IDS,
  INSPECTION_SOURCE_IDS,
  setDefaultMapLayersVisibility,
  setInspectionData,
  setInspectionSelection,
  setInspectionVisibility,
  startEmergencyPulse,
} from "./inspection/inspectionLayers";
import { InspectionModeControls } from "./inspection/InspectionControls";
import InspectionInfoPanel from "./inspection/InspectionPanels";
import { currentYear, INSPECTION_MODE } from "./inspection/inspectionStatus";

// === для рисования границ ПС ===
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import * as turf from "@turf/turf";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";

// === Video Instruction Modal ===
const DIRECTUS_URL = process.env.REACT_APP_DIRECTUS_URL || "https://map.uzbekistonmet.uz";

// Захардкоженные ID файлов из Directus (заменить после загрузки)
const VIDEO_IDS = {
  addPole: "4b8595cf-5fdc-4eb1-bccb-e678a1d4f758", // видео "Таянч қўшиш"
};

function VideoModal({ open, onClose, title, videoId }) {
  const videoRef = React.useRef(null);

  React.useEffect(() => {
    if (!open && videoRef.current) {
      videoRef.current.pause();
    }
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const src = `${DIRECTUS_URL}/assets/${videoId}`;

  return (
    <div className="video-modal-overlay" onClick={onClose}>
      <div className="video-modal" onClick={(e) => e.stopPropagation()}>
        <div className="video-modal__header">
          <span className="video-modal__title">{title}</span>
          <button className="video-modal__close" onClick={onClose}>×</button>
        </div>
        <div className="video-modal__body">
          <video
            ref={videoRef}
            src={src}
            controls
            autoPlay
            playsInline
            className="video-modal__player"
          />
        </div>
      </div>
    </div>
  );
}

// === KNOBS ===
const POLE_ACTIVE_ZOOM = 12; // с какого зума опоры кликабельны/видимы

// стили карты
const getStyleURL = (mode) => {
  if (mode === "streets") return "mapbox://styles/mapbox/streets-v12";
  if (mode === "satellite") return "mapbox://styles/mapbox/satellite-streets-v12";

  // Google — raster tile подложка + Mapbox labels overlay
  return {
    version: 8,
    sources: {
      "raster-tiles": {
        type: "raster",
        tiles: [
          "https://mt0.google.com/vt/lyrs=s&x={x}&y={y}&z={z}",
          "https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}",
          "https://mt2.google.com/vt/lyrs=s&x={x}&y={y}&z={z}",
          "https://mt3.google.com/vt/lyrs=s&x={x}&y={y}&z={z}",
        ],
        tileSize: 256,
        maxzoom: 21,
      },
      "mapbox-streets": {
        type: "vector",
        url: "mapbox://mapbox.mapbox-streets-v8",
      },
    },
    layers: [
      {
        id: "raster-base",
        type: "raster",
        source: "raster-tiles",
        paint: { "raster-fade-duration": 150 },
      },
      // границы стран — тень (обводка)
      {
        id: "admin-0-boundary-bg",
        type: "line",
        source: "mapbox-streets",
        "source-layer": "admin",
        filter: ["==", ["get", "admin_level"], 0],
        paint: {
          "line-color": "rgba(0, 0, 0, 0.5)",
          "line-width": 4,
        },
      },
      // границы стран — основная
      {
        id: "admin-0-boundary",
        type: "line",
        source: "mapbox-streets",
        "source-layer": "admin",
        filter: ["==", ["get", "admin_level"], 0],
        paint: {
          "line-color": "rgba(220, 220, 220, 0.9)",
          "line-width": 2,
        },
      },
      // границы регионов — тень
      {
        id: "admin-1-boundary-bg",
        type: "line",
        source: "mapbox-streets",
        "source-layer": "admin",
        filter: ["==", ["get", "admin_level"], 1],
        paint: {
          "line-color": "rgba(0, 0, 0, 0.35)",
          "line-width": 3,
        },
      },
      // границы регионов — основная
      {
        id: "admin-1-boundary",
        type: "line",
        source: "mapbox-streets",
        "source-layer": "admin",
        filter: ["==", ["get", "admin_level"], 1],
        paint: {
          "line-color": "rgba(200, 200, 200, 0.7)",
          "line-width": 1.2,
          "line-dasharray": [4, 2],
        },
      },
      // названия стран
      {
        id: "country-label",
        type: "symbol",
        source: "mapbox-streets",
        "source-layer": "place_label",
        filter: ["==", ["get", "class"], "country"],
        layout: {
          "text-field": ["get", "name"],
          "text-size": 14,
          "text-font": ["DIN Pro Medium", "Arial Unicode MS Regular"],
        },
        paint: {
          "text-color": "rgba(255, 255, 255, 0.8)",
          "text-halo-color": "rgba(0, 0, 0, 0.6)",
          "text-halo-width": 1.5,
        },
      },
      // названия регионов/городов
      {
        id: "place-label",
        type: "symbol",
        source: "mapbox-streets",
        "source-layer": "place_label",
        filter: ["in", ["get", "class"], ["literal", ["state", "city", "town"]]],
        layout: {
          "text-field": ["get", "name"],
          "text-size": ["match", ["get", "class"], "state", 12, "city", 11, 10],
          "text-font": ["DIN Pro Regular", "Arial Unicode MS Regular"],
        },
        paint: {
          "text-color": "rgba(255, 255, 255, 0.7)",
          "text-halo-color": "rgba(0, 0, 0, 0.5)",
          "text-halo-width": 1,
        },
      },
      // дороги основные
      {
        id: "road-label",
        type: "line",
        source: "mapbox-streets",
        "source-layer": "road",
        filter: ["in", ["get", "class"], ["literal", ["motorway", "trunk"]]],
        paint: {
          "line-color": "rgba(255, 255, 255, 0.15)",
          "line-width": 0.8,
        },
      },
    ],
    glyphs: "mapbox://fonts/mapbox/{fontstack}/{range}.pbf",
  };
};

const isNum = (v) => typeof v === "number" && Number.isFinite(v);
const isValidCoord = (lng, lat) => isNum(lng) && isNum(lat) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;

const isObj = (v) => v && typeof v === "object";

const normalizeLineCoords = (coords) =>
  (coords || [])
    .map(([lng, lat]) => [parseCoordinate(lng, "lng"), parseCoordinate(lat, "lat")])
    .filter(([lng, lat]) => isValidCoord(lng, lat));

const getNetworkCoords = (n, poles) => {
  // 1) если есть geometry — используем её
  const g = n?.geometry;

  if (isObj(g)) {
    if (g.type === "LineString") {
      const coords = normalizeLineCoords(g.coordinates);
      if (coords.length >= 2) return coords;
    }
    if (g.type === "MultiLineString") {
      // склеим все части в одну “визуальную линию”
      const merged = [];
      for (const part of g.coordinates || []) {
        const coords = normalizeLineCoords(part);
        if (coords.length) {
          // чтобы не дублировать стык
          if (merged.length && coords.length) {
            const [aLng, aLat] = merged[merged.length - 1];
            const [bLng, bLat] = coords[0];
            if (aLng === bLng && aLat === bLat) merged.pop();
          }
          merged.push(...coords);
        }
      }
      if (merged.length >= 2) return merged;
    }
  }

  // 2) fallback: старая логика по опорам
  const coords = sortPolesForLine(poles.filter((p) => p.network_id === n.id))
    .map((p) => [p.longitude, p.latitude])
    .filter(([lng, lat]) => isValidCoord(lng, lat));

  return coords.length >= 2 ? coords : null;
};

const getNetworkGeometry = (n, poles) => {
  const g = n?.geometry;

  if (g && typeof g === "object") {
    if (g.type === "LineString") {
      const coords = normalizeLineCoords(g.coordinates);
      if (coords.length >= 2) return { type: "LineString", coordinates: coords };
    }

    if (g.type === "MultiLineString") {
      const parts = (g.coordinates || [])
        .map((part) => normalizeLineCoords(part))
        .filter((part) => part.length >= 2);

      if (parts.length) return { type: "MultiLineString", coordinates: parts };
    }
  }

  // fallback по опорам
  const coords = sortPolesForLine(poles.filter((p) => p.network_id === n.id))
    .map((p) => [p.longitude, p.latitude])
    .filter(([lng, lat]) => isValidCoord(lng, lat));

  return coords.length >= 2 ? { type: "LineString", coordinates: coords } : null;
};

const sortPolesForLine = (arr) => {
  // null/undefined order_index уедет в конец
  return [...arr].sort((a, b) => {
    const ai = a?.order_index;
    const bi = b?.order_index;

    const aHas = ai !== null && ai !== undefined;
    const bHas = bi !== null && bi !== undefined;

    if (aHas && bHas) {
      if (ai !== bi) return ai - bi;
      // если одинаковый order_index — стабилизируем по id (или name)
      return (a.id ?? 0) - (b.id ?? 0);
    }

    if (aHas && !bHas) return -1;
    if (!aHas && bHas) return 1;

    // оба без order_index — стабилизируем по id
    return (a.id ?? 0) - (b.id ?? 0);
  });
};

// ===== Pole numbering helpers =====
const parsePoleName = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return { raw: "", base: null, suffix: "", hasSuffix: false };

  // 50, 50A, 50А, 50-A, 50-А
  const m = raw.match(/^\s*(\d+)\s*([-–])?\s*([A-Za-zА-Яа-яЁё]+)?\s*$/);
  if (!m) return { raw, base: null, suffix: "", hasSuffix: false };

  const base = Number(m[1]);
  if (!Number.isFinite(base)) return { raw, base: null, suffix: "", hasSuffix: false };

  const sep = m[2] ? "-" : ""; // нормализуем "–" в "-"
  const suf = (m[3] ?? "").trim();
  const suffix = suf ? `${sep}${suf.toUpperCase()}` : "";

  return { raw, base, suffix, hasSuffix: !!suf };
};

const formatPoleName = (base, suffix = "") => `${base}${suffix || ""}`;

/**
 * Умная генерация:
 * - старт берём из 1-й опоры (если пусто -> 1)
 * - направление берём из 2-й (если задана и != первой): +1 / -1
 * - опоры вида 50A/50А считаем "вставками": base совпадает с текущим номером -> НЕ двигают счётчик
 * - остальные (пустые/цифровые/любой текст) перезаписываем ожидаемым номером
 */
const generatePoleNumbersSmart = (arr) => {
  if (!Array.isArray(arr) || arr.length === 0) return arr;

  const p0 = parsePoleName(arr[0]?.name);
  const p1 = arr.length > 1 ? parsePoleName(arr[1]?.name) : null;

  let current = (p0.base ?? 1);
  let step = 1;

  if (p0.base != null && p1?.base != null && p1.base !== p0.base) {
    step = p1.base > p0.base ? 1 : -1;
  }

  return arr.map((p, idx) => {
    const parsed = parsePoleName(p?.name);

    // 1-я опора
    if (idx === 0) {
      const base = parsed.base ?? current;
      current = base;
      const name = parsed.base != null ? formatPoleName(parsed.base, parsed.suffix) : String(base);
      return { ...p, name };
    }

    // 2-я опора
    if (idx === 1) {
      if (parsed.base != null) {
        current = parsed.base;
        return { ...p, name: formatPoleName(parsed.base, parsed.suffix) };
      }
      const next = current + step;
      current = next;
      return { ...p, name: String(next) };
    }

    // Вставка типа 50A/50А: если base совпадает с текущим номером — оставляем и НЕ двигаем счётчик
    if (parsed.base != null && parsed.hasSuffix) {
      if (parsed.base === current) {
        return { ...p, name: formatPoleName(parsed.base, parsed.suffix) };
      }

      // если вдруг suffix стоит на "следующем" базовом числе — считаем, что пользователь так задумал
      // и переносим current на эту базу (но номер без суффикса потом всё равно появится дальше по шагу)
      if (parsed.base === current + step) {
        current = parsed.base;
        return { ...p, name: formatPoleName(parsed.base, parsed.suffix) };
      }

      // конфликтный случай: приводим вставку к текущему номеру + её суффиксу
      return { ...p, name: formatPoleName(current, parsed.suffix) };
    }

    // Всё остальное — выставляем ожидаемый номер
    const next = current + step;
    current = next;
    return { ...p, name: String(next) };
  });
};

const CustomMap = () => {

  const { permissions, isSuper, authLoading } = useContext(UserContext);

  const canMapCreate = useMemo(() => {
    if (authLoading) return false;
    if (isSuper) return true;
    return Array.isArray(permissions) && permissions.includes("MAP_CREATE");
  }, [permissions, isSuper, authLoading]);

  const canInspectionMode = useMemo(() => {
    if (authLoading) return false;
    if (isSuper) return true;
    return (
      Array.isArray(permissions) &&
      permissions.includes("POLE_CHECKUP_READ") &&
      permissions.includes("POLE_CHECKUP_SUSPICIOUS_READ")
    );
  }, [permissions, isSuper, authLoading]);

  const mapContainer = useRef(null);
  const map = useRef(null);
  const [mapReady, setMapReady] = useState(false);

  const [substations, setSubstations] = useState([]);
  const [poles, setPoles] = useState([]);
  const [cities, setCities] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // базовый стиль: satellite | streets
  const [baseStyle, setBaseStyle] = useState("google");

  const [mapMode, setMapMode] = useState(INSPECTION_MODE.normal);
  const [inspectionYear, setInspectionYear] = useState(currentYear());
  const [onlyUninspected, setOnlyUninspected] = useState(false);
  const [inspectionSummary, setInspectionSummary] = useState(null);
  const [inspectionSummaryLoading, setInspectionSummaryLoading] = useState(false);
  const [inspectionLoading, setInspectionLoading] = useState(false);
  const [inspectionError, setInspectionError] = useState("");
  const [inspectionTruncated, setInspectionTruncated] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [selectedInspectionLineId, setSelectedInspectionLineId] = useState(null);
  const [selectedInspectionPoleId, setSelectedInspectionPoleId] = useState(null);

  const inspectionRequestRef = useRef(null);
  const inspectionSummaryAbortRef = useRef(null);
  const inspectionDetailsAbortRef = useRef(null);
  const inspectionDetailsRequestRef = useRef(0);
  const inspectionDebounceRef = useRef(null);
  const inspectionPulseStopRef = useRef(null);
  const inspectionModeRef = useRef({ enabled: false, year: currentYear(), onlyUninspected: false });
  const inspectionOpenLineRef = useRef(null);
  const inspectionOpenPoleRef = useRef(null);

  // поиск
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const searchBoxRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const searchAbortRef = useRef(null);
  const searchRequestRef = useRef(0);
  const MAX_SEARCH_RESULTS = 40;

  // фильтры: кнопка + чекбоксы
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [infoOpen, setInfoOpen] = useState(true);

  // === Добавление опор (черновик) ===
  const [polesEditorOpen, setPolesEditorOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [draftPoles, setDraftPoles] = useState([]); // [{tmpId, name, latitude, longitude}]
  const [draftSearch, setDraftSearch] = useState("");
  const [pickPoleMode, setPickPoleMode] = useState(false); // ждём клик по карте
  const [mobileEditorHidden, setMobileEditorHidden] = useState(false); // скрыть панель на мобилке при добавлении опоры
  const [draftNetworkId, setDraftNetworkId] = useState(null);
  const [draftCityId, setDraftCityId] = useState(null);
  const [draftError, setDraftError] = useState("");
  const [draftNetworkTitle, setDraftNetworkTitle] = useState("");
  const [draftCityTitle, setDraftCityTitle] = useState("");

  const pickPoleModeRef = useRef(false);
  const polesEditorOpenRef = useRef(false);

  useEffect(() => { pickPoleModeRef.current = pickPoleMode; }, [pickPoleMode]);
  useEffect(() => { polesEditorOpenRef.current = polesEditorOpen; }, [polesEditorOpen]);

  const [saving, setSaving] = useState(false);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);

  const canSaveDraft = useMemo(() => {
    if (saving) return false;
    if (!draftNetworkId || !draftCityId) return false;
    if (!draftPoles.length) return false;
    return draftPoles.every(p => String(p.name || "").trim() && isValidCoord(p.longitude, p.latitude));
  }, [saving, draftNetworkId, draftCityId, draftPoles]);

  // моб. меню управления (поиск/режим/фильтры)
  const [controlsOpen, setControlsOpen] = useState(false);
  const [showLines, setShowLines] = useState(true);
  const [showLine500, setShowLine500] = useState(false);
  const [showLine220, setShowLine220] = useState(false);
  const [showLine110, setShowLine110] = useState(false);
  const [showLine35, setShowLine35] = useState(false);

  const [showSubs, setShowSubs] = useState(true);
  const [showSub500, setShowSub500] = useState(false);
  const [showSub220, setShowSub220] = useState(false);
  const [showSub110, setShowSub110] = useState(false);
  const [showSub35, setShowSub35] = useState(false);

  // левая инфо-панель (по умолчанию скрыта)
  const [info, setInfo] = useState(null); // {type, props, lngLat?}

  const selectedRef = useRef(null);

  // === для рисования границ ПС ===
  const [substationAreas, setSubstationAreas] = useState([]); // массив Feature
  const drawRef = useRef(null);
  const drawingSubIdRef = useRef(null);

  const clearSelection = () => {
    if (!map.current || !selectedRef.current) return;
    const { source, id } = selectedRef.current;
    try { map.current.setFeatureState({ source, id }, { selected: false }); } catch {}
    selectedRef.current = null;
  };
  const selectFeature = (source, id) => {
    if (!map.current || id == null) return;
    clearSelection();
    try {
      map.current.setFeatureState({ source, id }, { selected: true });
      selectedRef.current = { source, id };
    } catch {}
  };

  const makeInspectionPoleHighlight = useCallback((pole) => {
    const lng = parseCoordinate(pole?.lng ?? pole?.longitude, "lng");
    const lat = parseCoordinate(pole?.lat ?? pole?.latitude, "lat");
    if (!isValidCoord(lng, lat)) return null;
    return {
      type: "Feature",
      id: Number(pole?.id),
      geometry: { type: "Point", coordinates: [lng, lat] },
      properties: { id: Number(pole?.id) },
    };
  }, []);

  const makeInspectionLineHighlight = useCallback((id, geometry, properties = {}) => {
    if (!geometry || !["LineString", "MultiLineString"].includes(geometry.type)) return null;
    return {
      type: "Feature",
      id: Number(id),
      geometry,
      properties: { id: Number(id), ...properties },
    };
  }, []);

  const setInspectionHighlight = useCallback(({ line = null, pole = null, lineId = null, poleId = null }) => {
    setSelectedInspectionLineId(lineId ?? (line?.id ?? null));
    setSelectedInspectionPoleId(poleId ?? (pole?.id ?? null));
    if (map.current) {
      addInspectionLayers(map.current);
      setInspectionSelection(map.current, { line, pole });
    }
  }, []);

  const clearInspectionHighlight = useCallback(() => {
    setSelectedInspectionLineId(null);
    setSelectedInspectionPoleId(null);
    if (map.current) clearInspectionSelection(map.current);
  }, []);

  const fitInspectionLineGeometry = useCallback((geometry, bbox) => {
    if (!map.current) return false;

    const parsedBox = Array.isArray(bbox) && bbox.length === 4
      ? bbox.map((value, index) => parseCoordinate(value, index % 2 === 0 ? "lng" : "lat"))
      : null;
    const box = parsedBox?.every(Number.isFinite) ? parsedBox : null;

    try {
      const b = box || (geometry ? turf.bbox({ type: "Feature", geometry, properties: {} }) : null);
      if (b && b.every(Number.isFinite)) {
        map.current.fitBounds(
          [
            [b[0], b[1]],
            [b[2], b[3]],
          ],
          { padding: 90, maxZoom: 15, duration: 900 }
        );
        return true;
      }
    } catch {}

    const coords =
      geometry?.type === "LineString"
        ? geometry.coordinates
        : (geometry?.coordinates?.[0] || []);
    const first = coords?.[0];
    if (first && isValidCoord(first[0], first[1])) {
      map.current.flyTo({
        center: first,
        zoom: Math.max(map.current.getZoom(), 14),
        duration: 900,
        essential: true,
      });
      return true;
    }

    return false;
  }, []);

  const inspectionActive = mapMode === INSPECTION_MODE.inspection && canInspectionMode;

  useEffect(() => {
    inspectionModeRef.current = {
      enabled: inspectionActive,
      year: inspectionYear,
      onlyUninspected,
    };
  }, [inspectionActive, inspectionYear, onlyUninspected]);

  useEffect(() => {
    if (!canInspectionMode && mapMode === INSPECTION_MODE.inspection) {
      setMapMode(INSPECTION_MODE.normal);
    }
  }, [canInspectionMode, mapMode]);

  const getInspectionBbox = useCallback(() => {
    if (!map.current) return null;
    const b = map.current.getBounds();
    return {
      minLng: b.getWest(),
      minLat: b.getSouth(),
      maxLng: b.getEast(),
      maxLat: b.getNorth(),
    };
  }, []);

  const loadInspectionSummary = useCallback(async () => {
    if (!inspectionActive) return;

    inspectionSummaryAbortRef.current?.abort?.();
    const controller = new AbortController();
    inspectionSummaryAbortRef.current = controller;

    setInspectionSummaryLoading(true);
    try {
      const data = await fetchInspectionSummary({ year: inspectionYear, signal: controller.signal });
      if (!inspectionModeRef.current.enabled) return;
      setInspectionSummary(data);
    } catch (err) {
      if (err?.name !== "AbortError" && inspectionModeRef.current.enabled) {
        setInspectionError(err?.status === 403 ? "Кўрик режимига рухсат йўқ" : "Статистикани юклаб бўлмади");
      }
    } finally {
      if (!controller.signal.aborted) setInspectionSummaryLoading(false);
    }
  }, [inspectionActive, inspectionYear]);

  const loadInspectionViewport = useCallback(async () => {
    if (!inspectionActive || !map.current) return;

    addInspectionLayers(map.current);

    const bbox = getInspectionBbox();
    if (!bbox) return;

    const zoom = map.current.getZoom();
    const requestId = (inspectionRequestRef.current?.id || 0) + 1;
    inspectionRequestRef.current?.controller?.abort?.();

    const controller = new AbortController();
    inspectionRequestRef.current = { id: requestId, controller };

    setInspectionLoading(true);
    setInspectionError("");

    try {
      const [lineResult, poleResult] = await Promise.all([
        fetchInspectionLines({
          year: inspectionYear,
          bbox,
          zoom,
          onlyUninspected,
          signal: controller.signal,
        }),
        zoom >= POLE_ACTIVE_ZOOM
          ? fetchInspectionPoles({
              year: inspectionYear,
              bbox,
              zoom,
              onlyUninspected,
              signal: controller.signal,
            })
          : Promise.resolve({
              data: { type: "FeatureCollection", features: [] },
              count: 0,
              totalInBbox: 0,
              truncated: false,
            }),
      ]);

      if (inspectionRequestRef.current?.id !== requestId || !map.current || !inspectionModeRef.current.enabled) return;

      setInspectionData(map.current, {
        lines: lineResult?.data || { type: "FeatureCollection", features: [] },
        poles: poleResult?.data || { type: "FeatureCollection", features: [] },
      });
      setInspectionTruncated(!!(poleResult?.meta?.truncated ?? poleResult?.truncated));
    } catch (err) {
      if (err?.name !== "AbortError" && inspectionModeRef.current.enabled) {
        setInspectionError(err?.status === 403 ? "Кўрик режимига рухсат йўқ" : "Харита маълумотларини юклаб бўлмади");
      }
    } finally {
      if (inspectionRequestRef.current?.id === requestId && !controller.signal.aborted) {
        setInspectionLoading(false);
      }
    }
  }, [inspectionActive, inspectionYear, onlyUninspected, getInspectionBbox]);

  const scheduleInspectionViewportLoad = useCallback(() => {
    if (!inspectionModeRef.current.enabled) return;
    window.clearTimeout(inspectionDebounceRef.current);
    inspectionDebounceRef.current = window.setTimeout(() => {
      loadInspectionViewport();
    }, 320);
  }, [loadInspectionViewport]);

  const openInspectionLine = useCallback(async (id, options = {}) => {
    if (!inspectionActive || !id) return;

    inspectionDetailsAbortRef.current?.abort?.();
    const controller = new AbortController();
    inspectionDetailsAbortRef.current = controller;
    const requestId = inspectionDetailsRequestRef.current + 1;
    inspectionDetailsRequestRef.current = requestId;

    const initialLine = makeInspectionLineHighlight(id, options.geometry, options.properties);
    setInspectionHighlight({ line: initialLine, lineId: Number(id), pole: null, poleId: null });
    selectFeature(INSPECTION_SOURCE_IDS.lines, id);
    setSelectedInspection({ type: "line", id, loading: true, error: "", data: null });
    setInfoOpen(true);
    setControlsOpen(false);
    setFiltersOpen(false);

    try {
      const data = await fetchInspectionLineDetails({ id, year: inspectionYear, signal: controller.signal });
      if (inspectionDetailsRequestRef.current !== requestId || !inspectionModeRef.current.enabled) return;
      const fullLine = makeInspectionLineHighlight(id, data?.geometry, data?.line || {});
      if (fullLine) setInspectionHighlight({ line: fullLine, lineId: Number(id), pole: null, poleId: null });
      if (options.fitAfterLoad) fitInspectionLineGeometry(data?.geometry, data?.bbox);
      setSelectedInspection({ type: "line", id, loading: false, error: "", data });
    } catch (err) {
      if (err?.name !== "AbortError" && inspectionDetailsRequestRef.current === requestId && inspectionModeRef.current.enabled) {
        setSelectedInspection({
          type: "line",
          id,
          loading: false,
          data: null,
          error: err?.status === 403 ? "Линияга рухсат йўқ" : "Линияни юклаб бўлмади",
        });
      }
    }
  }, [inspectionActive, inspectionYear, fitInspectionLineGeometry, makeInspectionLineHighlight, setInspectionHighlight]);

  const openInspectionPole = useCallback(async (id, options = {}) => {
    if (!inspectionActive || !id) return;

    inspectionDetailsAbortRef.current?.abort?.();
    const controller = new AbortController();
    inspectionDetailsAbortRef.current = controller;
    const requestId = inspectionDetailsRequestRef.current + 1;
    inspectionDetailsRequestRef.current = requestId;

    const initialPole = makeInspectionPoleHighlight(options.pole);
    setInspectionHighlight({ pole: initialPole, poleId: Number(id), line: null, lineId: null });
    selectFeature(INSPECTION_SOURCE_IDS.poles, id);
    setSelectedInspection({ type: "pole", id, loading: true, error: "", data: null });
    setInfoOpen(true);
    setControlsOpen(false);
    setFiltersOpen(false);

    try {
      const data = await fetchInspectionPoleDetails({ id, year: inspectionYear, signal: controller.signal });
      if (inspectionDetailsRequestRef.current !== requestId || !inspectionModeRef.current.enabled) return;
      const fullPole = makeInspectionPoleHighlight(data?.pole);
      if (fullPole) setInspectionHighlight({ pole: fullPole, poleId: Number(id), line: null, lineId: null });
      if (options.focusAfterLoad && data?.pole?.hasCoordinates) {
        const lng = parseCoordinate(data.pole.longitude, "lng");
        const lat = parseCoordinate(data.pole.latitude, "lat");
        if (map.current && isValidCoord(lng, lat)) {
          map.current.flyTo({
            center: [lng, lat],
            zoom: Math.max(map.current.getZoom(), 15),
            duration: 900,
            essential: true,
          });
        }
      }
      setSelectedInspection({ type: "pole", id, loading: false, error: "", data });
    } catch (err) {
      if (err?.name !== "AbortError" && inspectionDetailsRequestRef.current === requestId && inspectionModeRef.current.enabled) {
        setSelectedInspection({
          type: "pole",
          id,
          loading: false,
          data: null,
          error: err?.status === 403 ? "Таянчга рухсат йўқ" : "Таянчни юклаб бўлмади",
        });
      }
    }
  }, [inspectionActive, inspectionYear, makeInspectionPoleHighlight, setInspectionHighlight]);

  const focusInspectionPoleFromList = useCallback((pole) => {
    const lat = parseCoordinate(pole?.lat, "lat");
    const lng = parseCoordinate(pole?.lng, "lng");
    if (!inspectionActive || !map.current || !isValidCoord(lng, lat)) return;

    map.current.flyTo({
      center: [lng, lat],
      zoom: Math.max(map.current.getZoom(), 15),
      duration: 900,
      essential: true,
    });

    openInspectionPole(pole.id, { pole });
  }, [inspectionActive, openInspectionPole]);

  useEffect(() => {
    inspectionOpenLineRef.current = openInspectionLine;
    inspectionOpenPoleRef.current = openInspectionPole;
  }, [openInspectionLine, openInspectionPole]);

  // const openPolesEditorForLine = () => {
  //   setDraftError("");

  //   if (!info || info.type !== "line") {
  //     setDraftError("Сначала выбери линию на карте.");
  //     return;
  //   }

  //   const networkId = Number(info.props?.id);
  //   if (!networkId) {
  //     setDraftError("Не удалось определить network_id линии.");
  //     return;
  //   }

  //   const n = networks.find((x) => Number(x.id) === networkId);
  //   const cityId = Number(n?.cityId ?? n?.city_id);

  //   if (!cityId) {
  //     setDraftError("Не удалось определить city_id у линии.");
  //     return;
  //   }

  //   setDraftNetworkId(networkId);
  //   setDraftCityId(cityId);

  //   setDraftPoles([]);
  //   setDraftSearch("");
  //   setPickPoleMode(false);

  //   setPolesEditorOpen(true);

  //   const cityName = n?.city?.name || cities.find((c) => Number(c.id) === Number(cityId))?.name || "Неизвестно";
  //   const kv = firstVoltageKv(n?.voltage);
  //   const lineTitle = `${n?.name ?? "—"}${kv ? ` ${kv}кВ` : ""}${cityName ? ` ${cityName}` : ""}`;

  //   setDraftNetworkTitle(lineTitle);
  //   setDraftCityTitle(cityName);
  //   setDraftSearch("");
  // };

  const firstVoltageKv = (raw) => {
    if (raw == null) return null;
    const firstPart = String(raw).trim().split("/")[0]?.trim() ?? "";
    const m = firstPart.match(/\d+/);
    if (!m) return null;
    const kv = Number(m[0]);
    return Number.isFinite(kv) ? kv : null;
  };

  const initDraftForNetwork = (networkId) => {
    const n = networks.find((x) => Number(x.id) === Number(networkId));
    if (!n) {
      setDraftError("Линия не найдена");
      return false;
    }

    const cityId = Number(n?.cityId ?? n?.city_id);
    if (!cityId) {
      setDraftError("Не удалось определить city_id у линии.");
      return false;
    }

    const cityName =
      n?.city?.name ||
      cities.find((c) => Number(c.id) === cityId)?.name ||
      "Неизвестно";

    const kv = firstVoltageKv(n?.voltage);
    const lineTitle = `${n?.name ?? "—"}${kv ? ` ${kv}кВ` : ""}${cityName ? ` ${cityName}` : ""}`;

    setDraftNetworkId(Number(n.id));
    setDraftCityId(cityId);

    setDraftNetworkTitle(lineTitle);
    setDraftCityTitle(cityName);

    return true;
  };

  const closePolesEditor = () => {
    setPolesEditorOpen(false);
    setPickPoleMode(false);
    setDraftError("");
    setDraftNetworkTitle("");
    setDraftCityTitle("");
    setConfirmClearOpen(false);
  };

  // const openPolesEditor = () => {
  //   setDraftError("");
  //   setDraftPoles([]);
  //   setDraftSearch("");
  //   setPickPoleMode(false);

  //   setDraftNetworkId(null);
  //   setDraftCityId(null);

  //   setPolesEditorOpen(true);

  //   setDraftNetworkTitle("");
  //   setDraftCityTitle("");
  // };

  const openPolesEditor = () => {
    setDraftError("");

    // всегда чистим черновик
    setDraftPoles([]);
    setPickPoleMode(false);

    // 1) если выбрана линия на карте — подставим её
    if (info?.type === "line") {
      const networkId = Number(info?.props?.id);
      if (networkId) {
        // подготовка draftNetworkId/draftCityId/заголовков
        const ok = initDraftForNetwork(networkId);
        if (ok) {
          setDraftSearch(""); // можно оставить пустым
          setPolesEditorOpen(true);
          return;
        }
        // если не смогли — просто откроем без выбранной линии
      }
    }

    // 2) иначе — открываем пусто, выбираешь через поиск
    setDraftNetworkId(null);
    setDraftCityId(null);
    setDraftNetworkTitle("");
    setDraftCityTitle("");

    setDraftSearch(""); // или оставь как было
    setPolesEditorOpen(true);
  };

  const handleGeneratePoleNumbers = () => {
    setDraftError("");
    setDraftPoles((prev) => generatePoleNumbersSmart(prev));
  };

  const handleSaveDraftPoles = async () => {
    setDraftError("");

    if (!draftNetworkId || !draftCityId) {
      setDraftError("Сначала выбери линию.");
      return;
    }
    if (!draftPoles.length) {
      setDraftError("Добавь хотя бы одну опору.");
      return;
    }
    for (const p of draftPoles) {
      if (!String(p.name || "").trim()) {
        setDraftError("У каждой опоры должно быть имя/номер.");
        return;
      }
      if (!isValidCoord(p.longitude, p.latitude)) {
        setDraftError("У одной из опор неверные координаты.");
        return;
      }
    }

    setSaving(true);
    try {
      // payload КАК ЖДЁТ КОНТРОЛЛЕР bulkCreatePoles
      const body = {
        network_id: Number(draftNetworkId),
        city_id: Number(draftCityId),
        poles: draftPoles.map((p) => ({
          name: String(p.name).trim(),
          latitude: p.latitude,
          longitude: p.longitude,
          
          pole_brand: p.pole_brand?.trim() || null,
          pole_type: p.pole_type?.trim() || null,
          wire_type: p.wire_type?.trim() || null,
          lightning_wire_type: p.lightning_wire_type?.trim() || null,
          optical_wire_type: p.optical_wire_type?.trim() || null,
          insulator_type: p.insulator_type?.trim() || null,
          fittings: p.fittings?.trim() || null,
          bird_protection: p.bird_protection?.trim() || null,
          // inspection: p.inspection?.trim() || null,
        })),
      };

      const res = await apiFetch("/api/poles/bulk", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`Не сохранилось: ${res.status} ${txt}`);
      }

      const json = await res.json(); // { message:"OK", poles:[...] }

      const created = (Array.isArray(json?.poles) ? json.poles : []).map((pole) => ({
        ...pole,
        latitude: parseCoordinate(pole.latitude, "lat"),
        longitude: parseCoordinate(pole.longitude, "lng"),
      }));
      setPoles((prev) => [...prev, ...created]);

      // закрываем редактор
      setDraftPoles([]);
      setPickPoleMode(false);
      setDraftSearch("");
      setPolesEditorOpen(false);
    } catch (e) {
      console.error(e);
      setDraftError(e?.message || "Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!map.current) return;
    const src = map.current.getSource("poles");
    if (!src) return;

    const polesGeo = {
      type: "FeatureCollection",
      features: (poles || [])
        .map((p, i) => {
          const coord = [p.longitude, p.latitude];
          if (!isValidCoord(coord[0], coord[1])) return null;

          const city = cities.find((c) => c.id === p.city_id);
          const net = networks.find((n) => n.id === p.network_id);
          const v = String(net?.voltage ?? "");

          const props = {
            id: p.id ?? i,
            name: p.name,
            city: city ? city.name : "Неизвестно",
            network: net ? net.name : "Неизвестно",
            network_id: p.network_id,
            network_slug: net ? net.slug : undefined,
          };
          if (v) props.voltage = v;

          return { id: p.id ?? i, type: "Feature", geometry: { type: "Point", coordinates: coord }, properties: props };
        })
        .filter(Boolean),
    };

    src.setData(polesGeo);
  }, [poles, cities, networks]);

  useEffect(() => {
    if (!map.current) return;

    const src = map.current.getSource("draft-poles");
    if (!src) return;

    const geo = {
      type: "FeatureCollection",
      features: (draftPoles || [])
        .filter((p) => isValidCoord(p.longitude, p.latitude))
        .map((p) => ({
          type: "Feature",
          geometry: { type: "Point", coordinates: [p.longitude, p.latitude] },
          properties: { tmpId: p.tmpId, name: p.name || "" },
        })),
    };

    src.setData(geo);
  }, [draftPoles]);

  const cityById = React.useMemo(() => {
    const m = new Map();
    for (const c of cities || []) m.set(String(c.id), c);
    return m;
  }, [cities]);

  const networkById = React.useMemo(() => {
    const m = new Map();
    for (const n of networks || []) m.set(String(n.id), n);
    return m;
  }, [networks]);

  const getCityName = (obj) => {
    const id = obj?.cityId ?? obj?.city_id ?? obj?.cityID;
    return obj?.city?.name || cityById.get(String(id))?.name || "Неизвестно";
  };

  const makeTitle = (name, kv, city) => {
    return `${name ?? "—"}${kv ? ` ${kv}кВ` : ""}${city ? ` ${city}` : ""}`;
  };

  const [networkSearchResults, setNetworkSearchResults] = useState([]);
  const draftSearchDebounceRef = useRef(null);

  useEffect(() => {
    const q = draftSearch.trim();
    if (!q || q.length < 2) {
      setNetworkSearchResults([]);
      return;
    }

    if (draftSearchDebounceRef.current) clearTimeout(draftSearchDebounceRef.current);

    draftSearchDebounceRef.current = setTimeout(async () => {
      try {
        const res = await apiFetch(`/api/search?query=${encodeURIComponent(q)}&limit=20`);
        const data = await res.json();
        if (!Array.isArray(data)) {
          setNetworkSearchResults([]);
          return;
        }

        const out = data
          .filter((item) => item.type === "network")
          .map((item) => {
            const d = item.data;
            const cityName = d.city?.name ?? "";
            const kv = firstVoltageKv(d.voltage);
            return {
              id: d.id,
              title: `${d.name}${kv ? ` ${kv}кВ` : ""}${cityName ? ` ${cityName}` : ""}`,
              cityId: d.cityId ?? d.city_id ?? null,
            };
          });

        setNetworkSearchResults(out);
      } catch (err) {
        console.error("Draft network search error:", err);
        setNetworkSearchResults([]);
      }
    }, 300);

    return () => {
      if (draftSearchDebounceRef.current) clearTimeout(draftSearchDebounceRef.current);
    };
  }, [draftSearch]);

  const selectDraftNetwork = (networkId) => {
    const n = networks.find((x) => Number(x.id) === Number(networkId));
    if (!n) {
      setDraftError("Линия не найдена");
      return;
    }

    const cityId = Number(n.cityId ?? n.city_id);
    const city = cities.find((c) => Number(c.id) === Number(cityId));

    const cityName = n.city?.name || city?.name || "Неизвестно";
    const kv = firstVoltageKv(n?.voltage);
    const lineTitle = `${n.name}${kv ? ` ${kv}кВ` : ""}${cityName ? ` ${cityName}` : ""}`;

    setDraftNetworkId(Number(n.id));
    setDraftCityId(cityId || null);

    // показываем нормальные названия
    setDraftNetworkTitle(lineTitle);
    setDraftCityTitle(cityName);

    setDraftError("");
    setDraftSearch("");
  };

  // чтобы сортировка была “умнее”: сначала те что начинаются с запроса
  const matchRank = (text, qLower) => {
    const t = String(text ?? "").toLowerCase();
    const idx = t.indexOf(qLower);
    if (idx === 0) return 0;
    if (idx > 0) return 1;
    return 2;
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const clearSearch = () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = null;
    searchAbortRef.current?.abort?.();
    searchAbortRef.current = null;
    searchRequestRef.current += 1;
    setSearchQuery("");
    setSearchResults([]);
    setSearchLoading(false);
  };

  useEffect(() => {
    const raw = searchQuery;
    const q = raw.trim();
    if (!q) {
      searchAbortRef.current?.abort?.();
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }

    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    setSearchLoading(true);
    setSearchResults([]);

    searchDebounceRef.current = setTimeout(async () => {
      const requestId = searchRequestRef.current + 1;
      searchRequestRef.current = requestId;
      searchAbortRef.current?.abort?.();
      const controller = new AbortController();
      searchAbortRef.current = controller;
      setSearchLoading(true);

      try {
        // Parse pole search: "116 л-504" or "116 " (digits + space)
        const poleMatch = raw.match(/^(\d+)\s(.*)$/);

        let url = `/api/search?query=${encodeURIComponent(q)}&limit=${MAX_SEARCH_RESULTS}`;
        if (poleMatch) {
          const poleName = poleMatch[1];
          const lineQ = poleMatch[2].trim();
          url += `&pole=${encodeURIComponent(poleName)}`;
          if (lineQ) url += `&line=${encodeURIComponent(lineQ)}`;
        }

        const res = await apiFetch(url, { signal: controller.signal });
        const data = await res.json();
        if (searchRequestRef.current !== requestId || controller.signal.aborted) return;
        if (!Array.isArray(data)) {
          setSearchResults([]);
          return;
        }

        if (inspectionModeRef.current.enabled) {
          const out = data.map((item) => {
            const d = item?.data || {};

            if (item?.type === "pole") {
              const id = Number(d.id ?? d.pole_id ?? d.poleId ?? item.id);
              if (!Number.isInteger(id) || id <= 0) return null;
              const netName = d.network?.name ?? d.networkName ?? "";
              const kv = firstVoltageKv(d.network?.voltage ?? d.voltage);
              const cityName = d.city?.name ?? d.cityName ?? "";
              return {
                type: "pole",
                id,
                title: `Таянч ${d.name || id}${netName ? ` — ${netName}` : ""}${kv ? ` ${kv}кВ` : ""}${cityName ? ` ${cityName}` : ""}`,
                meta: "Таянч",
              };
            }

            if (item?.type === "network" || item?.type === "line") {
              const id = Number(d.id ?? d.network_id ?? d.networkId ?? item.id);
              if (!Number.isInteger(id) || id <= 0) return null;
              const cityName = d.city?.name ?? d.cityName ?? "";
              const kv = firstVoltageKv(d.voltage);
              return {
                type: "line",
                id,
                title: `${d.name || "Линия"}${kv ? ` ${kv}кВ` : ""}${cityName ? ` ${cityName}` : ""}`,
                meta: "Линия",
              };
            }

            return null;
          }).filter(Boolean);

          setSearchResults(out);
          return;
        }

        const out = data.map((item) => {
          const d = item.data;
          if (item.type === "pole") {
            const netName = d.network?.name ?? "";
            const kv = firstVoltageKv(d.network?.voltage);
            const cityName = d.city?.name ?? "";
            return {
              type: "pole",
              id: d.id,
              title: `Таянч №${d.name}${netName ? ` — ${netName}` : ""}${kv ? ` ${kv}кВ` : ""}${cityName ? ` ${cityName}` : ""}`,
              meta: "Опора",
            };
          }
          if (item.type === "substation") {
            const cityName = d.city?.name ?? "";
            const kv = firstVoltageKv(d.voltage);
            return {
              type: "substation",
              id: d.id,
              title: makeTitle(d.name, kv, cityName),
              meta: "Подстанция",
            };
          }
          if (item.type === "network") {
            const cityName = d.city?.name ?? "";
            const kv = firstVoltageKv(d.voltage);
            return {
              type: "network",
              id: d.id,
              title: makeTitle(d.name, kv, cityName),
              meta: "Линия",
            };
          }
          if (item.type === "city") {
            return {
              type: "city",
              id: d.id,
              title: d.name,
              meta: "Филиал",
            };
          }
          return null;
        }).filter(Boolean);

        setSearchResults(out);
      } catch (err) {
        if (err?.name === "AbortError") return;
        console.error("Search error:", err);
        setSearchResults([]);
      } finally {
        if (searchRequestRef.current === requestId && !controller.signal.aborted) {
          setSearchLoading(false);
        }
      }
    }, 300);

    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      searchAbortRef.current?.abort?.();
    };
  }, [searchQuery, inspectionActive, inspectionYear]);

  useEffect(() => {
    const onDown = (e) => {
      const root = searchBoxRef.current;
      if (!root) return;
      if (!root.contains(e.target)) setSearchResults([]);
    };

    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, []);

  const VOLT_ORDER = ["500", "220", "110", "35", "10", "6"];

  const extractVoltages = (v) => {
    if (v == null) return [];
    const s = String(v).replace(",", "."); // на всякий
    // найдём все известные напряжения внутри строки
    const found = VOLT_ORDER.filter((vv) => s.includes(vv));
    // если значение чисто число типа 220 без текста — тоже поймается includes
    return found;
  };

  const pickPrimaryVoltage = (list) => {
    // берём самое "старшее" по VOLT_ORDER
    for (const v of VOLT_ORDER) if (list.includes(v)) return v;
    return "";
  };

  const toNum = (v) => {
    if (v == null) return null;
    const n = Number(String(v).replace(",", "."));
    return Number.isFinite(n) ? n : null;
  };

  const moveToInspectionSearchResult = (result) => {
    if (!result?.id || result?.disabled) return;
    setSearchResults([]);

    if (result.type === "pole") {
      openInspectionPole(result.id, { focusAfterLoad: true });
      return;
    }

    if (result.type === "line" || result.type === "network") {
      openInspectionLine(result.id, { fitAfterLoad: true });
    }
  };

  const moveToResult = (result) => {
    if (!map.current) return;

    if (inspectionModeRef.current.enabled) {
      moveToInspectionSearchResult(result);
      return;
    }

    let center = null;
    let source = null;
    let id = null;
    let props = null;
    let infoType = null;

    // ---------- ПОДСТАНЦИЯ ----------
    if (result.type === "substation") {
      // берём актуальный объект из стейта, на случай если в поиске был старый
      const s = substations.find((x) => x.id === result.id) || result;

      center = [s.longitude, s.latitude];
      source = "substations";
      id = s.id;

      props = {
        id: s.id,
        name: s.name,
        slug: s.slug,
        image: s.image, // если хочешь оставить картинку
        branch: s.city?.name,     // <-- филиал
        address: s.address,
        voltage: s.voltage != null && s.voltage !== "" ? String(s.voltage) : "",
        start_year: s.start_year,
        power_mva: s.power_mva,
        longitude: s.longitude,
        latitude: s.latitude,
      };
      if (s.voltage != null && s.voltage !== "") {
        props.voltage = String(s.voltage);
      }

      infoType = "substation";
    }

    // ---------- ОПОРА ----------
    else if (result.type === "pole") {
      const p = poles.find((x) => x.id === result.id) || result;

      center = [p.longitude, p.latitude];
      source = "poles";
      id = p.id;

      const city = cities.find((c) => c.id === p.city_id);
      const net = networks.find((n) => n.id === p.network_id);
      const v = String(net?.voltage ?? "");

      props = {
        id: p.id,
        name: p.name,
        city: city ? city.name : "Неизвестно",
        network: net ? net.name : "Неизвестно",
        network_id: p.network_id,
        network_slug: net ? net.slug : undefined,
        pole_brand: p.pole_brand,
        pole_type: p.pole_type,
        wire_type: p.wire_type,
        lightning_wire_type: p.lightning_wire_type,
        optical_wire_type: p.optical_wire_type,
        insulator_type: p.insulator_type,
        fittings: p.fittings,
        bird_protection: p.bird_protection,
        inspection: p.inspection,
      };
      if (v) props.voltage = v;

      infoType = "pole";
    }

    // ---------- ЛИНИЯ (сеть) ----------
    else if (result.type === "network") {
      const n = networks.find((x) => x.id === result.id) || result;

      source = "lines";
      id = n.id;

      const cityId = n.cityId ?? n.city_id;
      const city = cities.find((c) => String(c.id) === String(cityId));
      const cityName = n.city?.name || city?.name || "Неизвестно";
      const v = String(n.voltage ?? "");

      props = {
        id: n.id,
        name: n.name,
        city: cityName,
        slug: n.slug,
        voltage: v,
        length: n.length ?? "Неизвестно",
        start_year: n.start_year,
        span: n.span,
        wire_type: n.wire_type,
        wire_protection_type: n.wire_protection_type,
        optical_wire_type: n.optical_wire_type,
        poles_quantity: n.poles_quantity,
        intersections: n.intersections,
      };

      infoType = "line";

      // 1) СНАЧАЛА пробуем как раньше — по опорам
      const poleCoords = sortPolesForLine(poles.filter((p) => p.network_id === n.id))
        .map((p) => [p.longitude, p.latitude])
        .filter(([lng, lat]) => isValidCoord(lng, lat));

      if (poleCoords.length) {
        center = poleCoords[0];
        map.current.flyTo({ center, zoom: 14 }); // как раньше
      } else {
        // 2) Если опор нет — используем geometry
        const geom = getNetworkGeometry(n, poles);
        if (!geom) return;

        const feature = { type: "Feature", geometry: geom, properties: {} };

        try {
          const b = turf.bbox(feature); // [minLng, minLat, maxLng, maxLat]
          map.current.fitBounds(
            [
              [b[0], b[1]],
              [b[2], b[3]],
            ],
            { padding: 80, maxZoom: 14 }
          );

          const c = turf.center(feature).geometry.coordinates; // [lng, lat]
          center = c;
        } catch {
          // fallback на первую точку геометрии
          const coords =
            geom.type === "LineString"
              ? geom.coordinates
              : (geom.coordinates?.[0] || []);
          if (!coords?.length) return;
          center = coords[0];
          map.current.flyTo({ center, zoom: 14 });
        }
      }
    }

    if (center && isValidCoord(center[0], center[1])) {
      map.current.flyTo({ center, zoom: 14 });
      if (id != null && source) selectFeature(source, id);
      setInfo({
        type: infoType,
        props,
        lngLat: { lng: center[0], lat: center[1] },
      });
    }
  };

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const apply = () => setIsMobile(mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);

  // === загрузка/сохранение контуров локально ===
  useEffect(() => {
    try {
      const raw = localStorage.getItem("substationAreas");
      if (raw) setSubstationAreas(JSON.parse(raw));
    } catch {}
  }, []);
  useEffect(() => {
    try {
      localStorage.setItem("substationAreas", JSON.stringify(substationAreas));
    } catch {}
  }, [substationAreas]);

  // загрузка данных
  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const [sRes, pRes, cRes, nRes] = await Promise.all([
          apiFetch("/api/substation/", { cache: "no-store" }),
          apiFetch("/api/poles", { cache: "no-store" }),
          apiFetch("/api/city", { cache: "no-store" }),
          apiFetch("/api/network", { cache: "no-store" }),
        ]);

        const [s, p, c, n] = await Promise.all([
          sRes.json(),
          pRes.json(),
          cRes.json(),
          nRes.json(),
        ]);

        if (!alive) return;

        setSubstations(
          (Array.isArray(s) ? s : []).map((ss) => ({
            ...ss,
            latitude: parseCoordinate(ss.latitude, "lat"),
            longitude: parseCoordinate(ss.longitude, "lng"),
          }))
        );
        setPoles(
          (Array.isArray(p) ? p : []).map((pp) => ({
            ...pp,
            latitude: parseCoordinate(pp.latitude, "lat"),
            longitude: parseCoordinate(pp.longitude, "lng"),
          }))
        );
        setCities(Array.isArray(c) ? c : []);
        setNetworks(Array.isArray(n) ? n : []);
      } catch (e) {
        console.error(e);
        if (!alive) return;

        // чтобы карта не зависла в loading при ошибке
        setSubstations([]);
        setPoles([]);
        setCities([]);
        setNetworks([]);
      } finally {
        if (alive) setIsLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  // useEffect(() => {
  //   if (substations.length && poles.length && cities.length && networks.length) setIsLoading(false);
  // }, [substations, poles, cities, networks]);

  // helper для обновления источника с полигонами
  const refreshAreasSource = () => {
    if (!map.current) return;
    const src = map.current.getSource("substation-areas");
    if (src) {
      src.setData({ type: "FeatureCollection", features: substationAreas });
    }
  };
  useEffect(() => { refreshAreasSource(); }, [substationAreas]);

  // общий хелпер: добавить ВСЕ источники и слои (без обработчиков событий/контролов!)
  const reAddCustomLayersForCurrentStyle = () => {
    if (!map.current) return;

    // --- подготовка GeoJSON на основе текущих стейтов ---
    const substationsGeo = {
      type: "FeatureCollection",
      features: substations
        .map((s, i) => {
          const coord = [s.longitude, s.latitude];
          if (!isValidCoord(coord[0], coord[1])) return null;

          const volts = extractVoltages(s.voltage);
          const firstKv = firstVoltageKv(s.voltage);  

          const props = {
            id: s.id ?? i,
            name: s.name,
            slug: s.slug,
            image: s.image,
            branch: s.city?.name,
            address: s.address,
            start_year: s.start_year,
            power_mva: s.power_mva,
            longitude: s.longitude,
            latitude: s.latitude,

            // важно для фильтров:
            voltage: firstKv != null ? String(firstKv) : "", // <-- ВАЖНО: строка "500"/"220"/"110"
            voltage_set: volts.join("|"),         // набор: "500|220" и т.п.
            voltage_raw: s.voltage != null ? String(s.voltage) : "",  // ← для инфо
          };

          return {
            id: s.id ?? i,
            type: "Feature",
            geometry: { type: "Point", coordinates: coord },
            properties: props,
          };
        })
        .filter(Boolean),
    };

    const polesGeo = {
      type: "FeatureCollection",
      features: poles
        .map((p, i) => {
          const city = cities.find((c) => c.id === p.city_id);
          const net = networks.find((n) => n.id === p.network_id);
          const v = String(net?.voltage ?? "");
          const coord = [p.longitude, p.latitude];
          if (!isValidCoord(coord[0], coord[1])) return null;
          const props = {
            id: p.id ?? i,
            name: p.name,
            city: city ? city.name : "Неизвестно",
            network: net ? net.name : "Неизвестно",
            network_id: p.network_id,
            network_slug: net ? net.slug : undefined,
            pole_brand: p.pole_brand,
            pole_type: p.pole_type,
            wire_type: p.wire_type,
            lightning_wire_type: p.lightning_wire_type,
            optical_wire_type: p.optical_wire_type,
            insulator_type: p.insulator_type,
            fittings: p.fittings,
            bird_protection: p.bird_protection,
            inspection: p.inspection,
          };
          if (v) props.voltage = v;
          return { id: p.id ?? i, type: "Feature", geometry: { type: "Point", coordinates: coord }, properties: props };
        })
        .filter(Boolean),
    };

    const linesGeo = {
      type: "FeatureCollection",
      features: networks
        .map((n, i) => {
          const geom = getNetworkGeometry(n, poles);
          if (!geom) return null;

          const cityId = n.cityId ?? n.city_id;
          const city = cities.find((c) => String(c.id) === String(cityId));
          const cityName = n.city?.name || city?.name || "Неизвестно";
          const v = String(n.voltage ?? "");
          const volts = extractVoltages(v);
          const primary = pickPrimaryVoltage(volts);

          return {
            id: n.id ?? i,
            type: "Feature",
            geometry: geom,
            properties: {
              id: n.id ?? i,
              name: n.name,
              city: cityName,
              slug: n.slug,

              voltage: primary || v,
              voltage_set: volts.join("|"),

              length: n.length ?? "Неизвестно",
              start_year: n.start_year,
              span: n.span,
              wire_type: n.wire_type,
              wire_protection_type: n.wire_protection_type,
              optical_wire_type: n.optical_wire_type,
              poles_quantity: n.poles_quantity,
              intersections: n.intersections,

              // чисто полезно для дебага
              has_geometry: !!n?.geometry,
            },
          };
        })
        .filter(Boolean),
    };

    const getLineStartEnd = (geom) => {
      if (!geom) return null;

      if (geom.type === "LineString") {
        const c = geom.coordinates;
        if (!c?.length) return null;
        return { start: c[0], end: c[c.length - 1] };
      }

      if (geom.type === "MultiLineString") {
        const parts = geom.coordinates || [];
        if (!parts.length) return null;

        const first = parts[0];
        const last = parts[parts.length - 1];
        if (!first?.length || !last?.length) return null;

        return { start: first[0], end: last[last.length - 1] };
      }

      return null;
    };

    const lineEndsGeo = {
      type: "FeatureCollection",
      features: networks.flatMap((n) => {
        const geom = getNetworkGeometry(n, poles);
        const se = getLineStartEnd(geom);
        if (!se) return [];

        const { start, end } = se;

        return [
          { type: "Feature", geometry: { type: "Point", coordinates: start }, properties: { type: "start", network: n.name } },
          { type: "Feature", geometry: { type: "Point", coordinates: end },   properties: { type: "end",   network: n.name } },
        ];
      }),
    };

    // === пин-иконки подстанций ===
    const createPinImageData = (color, size = 24) => {
      const w = size;
      const h = size;
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");

      const cx = w / 2;
      const r = w * 0.32;         // радиус головы
      const headY = r + 1;        // центр головы
      const tipY = h - 1;         // кончик

      // капля: SVG-style path через bezier
      ctx.beginPath();
      ctx.moveTo(cx, tipY);
      // левая сторона от кончика вверх к кругу
      ctx.bezierCurveTo(
        cx - r * 0.2, tipY - r * 0.8,
        cx - r, headY + r * 0.8,
        cx - r, headY
      );
      // верхняя дуга (полукруг)
      ctx.arc(cx, headY, r, Math.PI, 0, false);
      // правая сторона от круга вниз к кончику
      ctx.bezierCurveTo(
        cx + r, headY + r * 0.8,
        cx + r * 0.2, tipY - r * 0.8,
        cx, tipY
      );
      ctx.closePath();

      ctx.fillStyle = color;
      ctx.fill();

      // обводка
      ctx.strokeStyle = "rgba(255,255,255,0.9)";
      ctx.lineWidth = 1.2;
      ctx.stroke();

      // белый кружок
      ctx.beginPath();
      ctx.arc(cx, headY, r * 0.38, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(255,255,255,0.9)";
      ctx.fill();

      const imgData = ctx.getImageData(0, 0, w, h);
      return { width: w, height: h, data: new Uint8Array(imgData.data.buffer) };
    };

    const pinColors = { "500": "#ff0000", "220": "#1fab15", "110": "#ff9900", "35": "#0000ff", default: "#800080" };
    for (const [key, color] of Object.entries(pinColors)) {
      const imgName = `pin-${key}`;
      if (map.current.hasImage(imgName)) map.current.removeImage(imgName);
      const pin = createPinImageData(color, 32);
      map.current.addImage(imgName, pin);
    }

    // === источники ===
    map.current.addSource("substations", { type: "geojson", data: substationsGeo, promoteId: "id" });
    map.current.addSource("poles",       { type: "geojson", data: polesGeo,       promoteId: "id" });
    map.current.addSource("lines",       { type: "geojson", data: linesGeo,       promoteId: "id" });
    map.current.addSource("line-ends",   { type: "geojson", data: lineEndsGeo });

    // общие выражения
    const selectedFactor = ["case", ["boolean", ["feature-state", "selected"], false], 1.6, 1];
    const selectedMult = ["case", ["boolean", ["feature-state", "selected"], false], 3, 1];
    const lineColorNormal = [
      "match",
      ["get", "voltage"],
      "500", "#ff0000",
      "220", "#1fab15",
      "110", "#ff9900",
      "35",  "#0000ff",
      "10",  "#7a00ff",
      "6",   "#00c2ff",
      /* default */ "#00ff00",
    ];
    const lineColorDark = [
      "match",
      ["get", "voltage"],
      "500", "#8B0000",
      "220", "#148A0F",
      "110", "#CC7A00",
      "35",  "#00008B",
      "10",  "#7a00ff",
      "6",   "#00c2ff",
      /* default */ "#008000",
    ];

    // слои линий
    map.current.addLayer({
      id: "line-layer",
      type: "line",
      source: "lines",
      layout: { "line-join": "round", "line-cap": "round" },
      paint: {
        "line-color": [
          "case",
          ["boolean", ["feature-state", "selected"], false],
          lineColorDark,
          lineColorNormal
        ],
        "line-width": [
          "interpolate", ["linear"], ["zoom"],
          6,  ["*", 1.5,  selectedMult],
          10, ["*", 3,    selectedMult],
          14, ["*", 6,    selectedMult],
          18, ["*", 12,   selectedMult]
        ]
      }
    });

    map.current.addLayer({
      id: "line-hit-layer",
      type: "line",
      source: "lines",
      layout: { "line-join": "round", "line-cap": "round" },
      paint: {
        "line-color": "#000000",
        "line-opacity": 0.01,
        "line-width": ["interpolate", ["linear"], ["zoom"], 6, 10, 10, 18, 14, 28, 18, 40],
      },
    });

    // слои опор
    map.current.addLayer({
      id: "pole-hit-layer",
      type: "circle",
      source: "poles",
      minzoom: POLE_ACTIVE_ZOOM,
      paint: {
        "circle-color": "#000000",
        "circle-opacity": 0.01,
        "circle-radius": ["interpolate", ["linear"], ["zoom"], POLE_ACTIVE_ZOOM, 14, 14, 16, 18, 22],
      },
    });

    map.current.addLayer({
      id: "pole-layer",
      type: "circle",
      source: "poles",
      minzoom: POLE_ACTIVE_ZOOM,
      paint: {
        "circle-color": ["case", ["boolean", ["feature-state", "selected"], false], "#FFD400", "#cccccc"],
        "circle-radius": ["interpolate", ["linear"], ["zoom"], 12, ["*", 5, selectedFactor], 14, ["*", 7, selectedFactor], 18, ["*", 10, selectedFactor]],
        "circle-stroke-color": "#666666",
        "circle-stroke-width": 1,
      },
    });

    // подстанции — пин-маркеры
    map.current.addLayer({
      id: "substation-layer",
      type: "symbol",
      source: "substations",
      layout: {
        "icon-image": [
          "match",
          ["get", "voltage"],
          "500", "pin-500",
          "220", "pin-220",
          "110", "pin-110",
          "35",  "pin-35",
          "pin-default"
        ],
        "icon-size": ["interpolate", ["linear"], ["zoom"],
          5, 0.4,
          8, 0.6,
          12, 0.8,
          16, 1.0
        ],
        "icon-anchor": "bottom",
        "icon-allow-overlap": true,
      },
    });

    map.current.addLayer({
      id: "substation-labels",
      type: "symbol",
      source: "substations",
      layout: {
        "text-field": ["get", "name"],
        "text-offset": [0, -2.8],
        "text-anchor": "top",
        "text-size": 12,
      },
      paint: {
        "text-color": [
          "case",
          ["boolean", ["feature-state", "selected"], false],
          "#800080",
          ["match",
            ["get", "voltage"],
            "500", "#ff0000",
            "220", "#1fab15",
            "110", "#ff9900",
            "35",  "#0000ff",
            /* default */ "#800080"
          ]
        ],
        "text-halo-color": "#ffffff",
        "text-halo-width": 2,
      },
    });

    // концы линий
    map.current.addLayer({
      id: "line-end-layer",
      type: "circle",
      source: "line-ends",
      minzoom: POLE_ACTIVE_ZOOM, // показывать только когда опоры уже активны
      paint: {
        "circle-color": "#ffff00",
        "circle-stroke-color": "#000",
        "circle-stroke-width": 1,
        "circle-radius": [
          "interpolate", ["linear"], ["zoom"],
          POLE_ACTIVE_ZOOM, 4,
          14, 7,
          18, 11
        ],
      },
    });

    // === Черновик опор (локально, только для визуализации) ===
    map.current.addSource("draft-poles", {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
    });

    map.current.addLayer({
      id: "draft-poles-layer",
      type: "circle",
      source: "draft-poles",
      paint: {
        "circle-color": "#00E5FF",
        "circle-radius": ["interpolate", ["linear"], ["zoom"], 6, 4, 12, 6, 16, 10],
        "circle-stroke-color": "#003B46",
        "circle-stroke-width": 2,
        "circle-opacity": 0.9,
      },
    });

    // контуры ПС
    map.current.addSource("substation-areas", {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
      promoteId: "id"
    });
    map.current.addLayer({
      id: "substation-areas-fill",
      type: "fill",
      source: "substation-areas",
      paint: { "fill-color": "transparent", "fill-opacity": 0.25 }
    });
    map.current.addLayer({
      id: "substation-areas-outline",
      type: "line",
      source: "substation-areas",
      paint: { "line-color": "#FF1493", "line-width": 2 }
    });

    // подставим сохранённые полигоны
    refreshAreasSource();

    // и сразу восстановим фильтры
    applyAllFilters();
  };

  // Построение карты
  useEffect(() => {
    if (isLoading) return;

    mapboxgl.accessToken = "pk.eyJ1IjoiamFtMWsiLCJhIjoiY202ZWs5djVlMGtkaTJtcXc2NzRxbzJ5bCJ9.-Eo5O0UQ6ND44g8QNhvU8g";
    try { mapboxgl.setTelemetry?.(false); } catch {}

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: getStyleURL(baseStyle), // <-- используем текущее значение
      center: [64.5857, 41.3775],
      zoom: 6,
      maxBounds: [[50.0, 35.0], [80.0, 50.0]],
    });

    // геолокация
    const geo = new mapboxgl.GeolocateControl({
      positionOptions: { enableHighAccuracy: true },
      trackUserLocation: true,
      showUserHeading: true,
    });
    map.current.addControl(geo, "bottom-right");

    map.current.on("load", () => {
      // добавляем источники/слои ПЕРВЫЙ раз
      reAddCustomLayersForCurrentStyle();
      addInspectionLayers(map.current);
      setInspectionVisibility(map.current, false);
      setMapReady(true);

      // === контрол рисования (добавляем ОДИН раз) ===
      const draw = new MapboxDraw({ displayControlsDefault: false, controls: { polygon: true, trash: true } });
      map.current.addControl(draw, "top-right");
      drawRef.current = draw;

      // события draw.create
      map.current.on("draw.create", (e) => {
        const f = e.features?.[0];
        if (!f) return;
        if (!["Polygon", "MultiPolygon"].includes(f.geometry?.type)) return;

        const feature = {
          type: "Feature",
          id: Date.now(),
          geometry: f.geometry,
          properties: { substation_id: drawingSubIdRef.current ?? null },
        };
        setSubstationAreas(prev => [...prev, feature]);

        try { draw.delete(f.id); } catch {}
      });

      map.current.on("draw.create", (e) => {
        const f = e.features?.[0];
        if (!f) return;
        try {
          const m2 = turf.area(f);
          console.log("Площадь (м²):", Math.round(m2));
        } catch {}
      });

      let moved = false;

      map.current.on("dragstart", () => { moved = true; });
      map.current.on("zoomstart", () => { moved = true; });
      map.current.on("rotatestart", () => { moved = true; });
      map.current.on("pitchstart", () => { moved = true; });

      map.current.on("dragend", () => { setTimeout(() => (moved = false), 200); });
      map.current.on("zoomend", () => { setTimeout(() => (moved = false), 200); });
      map.current.on("rotateend", () => { setTimeout(() => (moved = false), 200); });
      map.current.on("pitchend", () => { setTimeout(() => (moved = false), 200); });

      // ЕДИНЫЙ клик: опора -> подстанция -> линия -> иначе снять выделение
      const unifiedClick = (e) => {
        if (moved) return

        // если мы в режиме добавления опоры — клик по карте добавляет точку и НЕ трогает выделения
        if (pickPoleModeRef.current && polesEditorOpenRef.current) {
          const lng = parseCoordinate(e.lngLat.lng, "lng");
          const lat = parseCoordinate(e.lngLat.lat, "lat");

          setDraftPoles((prev) => [
            ...prev,
            {
              tmpId: `tmp_${Date.now()}_${Math.random().toString(16).slice(2)}`,
              name: "",
              longitude: lng,
              latitude: lat,
              lngStr: String(lng),
              latStr: String(lat),

              pole_brand: "",
              pole_type: "",
              wire_type: "",
              lightning_wire_type: "",
              optical_wire_type: "",
              insulator_type: "",
              fittings: "",
              bird_protection: "",
              // inspection: "",
            },
          ]);

          setPickPoleMode(false);
          setMobileEditorHidden(false); // вернуть панель на мобилке
          return;
        }

        // если сейчас идёт рисование полигона — не мешаем
        const mode = drawRef.current?.getMode?.();
        if (mode && mode !== "simple_select") return;

        if (inspectionModeRef.current.enabled) {
          if (map.current.getZoom() >= POLE_ACTIVE_ZOOM && map.current.getLayer(INSPECTION_LAYER_IDS.polesHit)) {
            const featsPole = map.current.queryRenderedFeatures(e.point, { layers: [INSPECTION_LAYER_IDS.polesHit] });
            if (featsPole.length) {
              const f = featsPole[0];
              const id = f.id ?? f.properties.id;
              const coords = f.geometry?.coordinates || [];
              inspectionOpenPoleRef.current?.(id, {
                pole: {
                  ...f.properties,
                  id,
                  lng: coords[0],
                  lat: coords[1],
                },
              });
              return;
            }
          }

          if (map.current.getLayer(INSPECTION_LAYER_IDS.linesHit)) {
            const featsLine = map.current.queryRenderedFeatures(e.point, { layers: [INSPECTION_LAYER_IDS.linesHit] });
            if (featsLine.length) {
              const f = featsLine[0];
              const id = f.id ?? f.properties.id;
              inspectionOpenLineRef.current?.(id, { geometry: f.geometry, properties: f.properties });
              return;
            }
          }

          clearSelection();
          clearInspectionHighlight();
          setSelectedInspection(null);
          setInfo(null);
          if (!isMobile) setInfoOpen(true);
          setControlsOpen(false);
          setFiltersOpen(false);
          return;
        }

        if (map.current.getZoom() >= POLE_ACTIVE_ZOOM) {
          const featsPole = map.current.queryRenderedFeatures(e.point, { layers: ["pole-hit-layer"] });
          if (featsPole.length) {
            const f = featsPole[0], id = f.id ?? f.properties.id, p = f.properties, c = e.lngLat;
            selectFeature("poles", id);
            setInfo({ type: "pole", props: p, lngLat: c });
            if (isMobile) setInfoOpen(false);  // <- на мобилке НЕ открываем панель сразу
            else setInfoOpen(true);
            setControlsOpen(false);
            setFiltersOpen(false);
            return;
          }
        }

        const featsSub = map.current.queryRenderedFeatures(e.point, { layers: ["substation-layer"] });
        if (featsSub.length) {
          const f = featsSub[0], id = f.id ?? f.properties.id, p = f.properties, c = e.lngLat;
          selectFeature("substations", id);
          setInfo({ type: "substation", props: p, lngLat: c });
          if (isMobile) setInfoOpen(false);  // <- на мобилке НЕ открываем панель сразу
          else setInfoOpen(true);
          setControlsOpen(false);
          setFiltersOpen(false);
          return;
        }

        const featsLine = map.current.queryRenderedFeatures(e.point, { layers: ["line-hit-layer"] });
        if (featsLine.length) {
          const f = featsLine[0], id = f.id ?? f.properties.id, p = f.properties, c = e.lngLat;
          selectFeature("lines", id);
          setInfo({ type: "line", props: p, lngLat: c });

          if (isMobile) setInfoOpen(false);  // <- на мобилке НЕ открываем панель сразу
          else setInfoOpen(true);
          setControlsOpen(false);
          setFiltersOpen(false);
          return;
        }

        clearSelection();
        setInfo(null);
        setControlsOpen(false);
        setFiltersOpen(false);
      };

      map.current.on("click", unifiedClick);
      map.current.on("touchend", unifiedClick);

      // курсор
      const updateCursor = (e) => {
        const z = map.current.getZoom();
        if (inspectionModeRef.current.enabled) {
          const hasPole =
            z >= POLE_ACTIVE_ZOOM &&
            map.current.getLayer(INSPECTION_LAYER_IDS.polesHit) &&
            map.current.queryRenderedFeatures(e.point, { layers: [INSPECTION_LAYER_IDS.polesHit] }).length > 0;
          const hasLine =
            !hasPole &&
            map.current.getLayer(INSPECTION_LAYER_IDS.linesHit) &&
            map.current.queryRenderedFeatures(e.point, { layers: [INSPECTION_LAYER_IDS.linesHit] }).length > 0;
          map.current.getCanvas().style.cursor = (hasPole || hasLine) ? "pointer" : "";
          return;
        }
        const hasPole = z >= POLE_ACTIVE_ZOOM && map.current.queryRenderedFeatures(e.point, { layers: ["pole-hit-layer"] }).length > 0;
        const hasSub  = !hasPole && map.current.queryRenderedFeatures(e.point, { layers: ["substation-layer"] }).length > 0;
        const hasLine = !hasPole && !hasSub && map.current.queryRenderedFeatures(e.point, { layers: ["line-hit-layer"] }).length > 0;
        map.current.getCanvas().style.cursor = (hasPole || hasSub || hasLine) ? "pointer" : "";
      };
      map.current.on("mousemove", updateCursor);

      // первичная отрисовка фильтров
      applyAllFilters();
    });

    return () => {
      setMapReady(false);
      window.clearTimeout(inspectionDebounceRef.current);
      inspectionRequestRef.current?.controller?.abort?.();
      inspectionSummaryAbortRef.current?.abort?.();
      inspectionDetailsAbortRef.current?.abort?.();
      inspectionDetailsRequestRef.current += 1;
      inspectionPulseStopRef.current?.();
      inspectionPulseStopRef.current = null;
      if (map.current) map.current.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  // когда меняем базовый стиль — просто setStyle и заново добавляем источники/слои
  useEffect(() => {
    if (!map.current) return;
    map.current.setStyle(getStyleURL(baseStyle));
    map.current.once("style.load", () => {
      reAddCustomLayersForCurrentStyle();
      addInspectionLayers(map.current);
      setInspectionVisibility(map.current, inspectionModeRef.current.enabled);
      setDefaultMapLayersVisibility(map.current, !inspectionModeRef.current.enabled);
      if (inspectionModeRef.current.enabled) scheduleInspectionViewportLoad();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseStyle]);

  // === фильтры для слоёв ===
  const buildVoltageSetFilter = (flags) => {
    const allowed = [];
    for (const v of VOLT_ORDER) if (flags[v]) allowed.push(v);

    // ничего не выбрано -> ничего не показываем
    if (!allowed.length) return ["==", ["get", "___never___"], "1"];

    // покажем если хотя бы одно из allowed входит в строку voltage_set ("500|220|...")
    return ["any", ...allowed.map((v) => ["in", v, ["get", "voltage_set"]])];
  };

  // фильтр подстанций — только по первому (главному) напряжению
  const buildFirstVoltageFilter = (flags) => {
    const allowed = [];
    for (const v of VOLT_ORDER) if (flags[v]) allowed.push(v);
    if (!allowed.length) return ["==", ["get", "___never___"], "1"];
    if (allowed.length === 1) return ["==", ["get", "voltage"], allowed[0]];
    return ["any", ...allowed.map((v) => ["==", ["get", "voltage"], v])];
  };

  // --------- ИЗМЕНЕНО: логика применения фильтров ----------
  const applyAllFilters = () => {
    if (!map.current || !map.current.getStyle()) return;

    // --- ЛИНИИ
    {
      const lineLayers = ["line-layer", "line-hit-layer"];
      for (const id of lineLayers) {
        if (!map.current.getLayer(id)) continue;
        map.current.setLayoutProperty(id, "visibility", "visible");
        if (showLines) {
          // показать всё
          map.current.setFilter(id, null);
        } else {
          const lineFilter = buildVoltageSetFilter({
            "500": showLine500,
            "220": showLine220,
            "110": showLine110,
            "35":  showLine35,
          });
          map.current.setFilter(id, lineFilter);
        }
      }
    }

    // --- ПОДСТАНЦИИ
    {
      const subLayers = ["substation-layer", "substation-labels"];
      for (const id of subLayers) {
        if (!map.current.getLayer(id)) continue;
        map.current.setLayoutProperty(id, "visibility", "visible");
        if (showSubs) {
          // показать всё
          map.current.setFilter(id, null);
        } else {
          const subMatch = buildFirstVoltageFilter({
            "500": showSub500,
            "220": showSub220,
            "110": showSub110,
            "35":  showSub35,
          });
          const subFilter = ["any", ["!", ["has", "voltage_set"]], subMatch];
          map.current.setFilter(id, subFilter);
        }
      }
    }
  };

  // реагируем на переключатели фильтров
  useEffect(() => {
    if (!mapReady || !map.current) return;

    addInspectionLayers(map.current);

    if (inspectionActive) {
      clearSelection();
      setDefaultMapLayersVisibility(map.current, false);
      setInspectionVisibility(map.current, true);
      setInfo(null);
      if (!isMobile) setInfoOpen(true);

      if (!inspectionPulseStopRef.current) {
        inspectionPulseStopRef.current = startEmergencyPulse(map.current);
      }

      loadInspectionSummary();
      loadInspectionViewport();
      return;
    }

    inspectionPulseStopRef.current?.();
    inspectionPulseStopRef.current = null;
    inspectionRequestRef.current?.controller?.abort?.();
    inspectionSummaryAbortRef.current?.abort?.();
    inspectionDetailsAbortRef.current?.abort?.();
    inspectionDetailsRequestRef.current += 1;
    window.clearTimeout(inspectionDebounceRef.current);
    setInspectionVisibility(map.current, false);
    clearInspectionData(map.current);
    clearInspectionHighlight();
    setDefaultMapLayersVisibility(map.current, true);
    applyAllFilters();
    setSelectedInspection(null);
    setInspectionError("");
    setInspectionTruncated(false);
  }, [mapReady, inspectionActive, isMobile, loadInspectionSummary, loadInspectionViewport, clearInspectionHighlight]);

  useEffect(() => {
    if (!mapReady || !map.current) return;
    const handler = () => {
      if (inspectionModeRef.current.enabled) scheduleInspectionViewportLoad();
    };
    map.current.on("moveend", handler);
    map.current.on("zoomend", handler);
    return () => {
      if (!map.current) return;
      map.current.off("moveend", handler);
      map.current.off("zoomend", handler);
    };
  }, [mapReady, scheduleInspectionViewportLoad]);

  useEffect(() => {
    if (!inspectionActive) return;
    clearSelection();
    clearInspectionHighlight();
    setSelectedInspection(null);
  }, [inspectionYear, inspectionActive, clearInspectionHighlight]);

  useEffect(() => {
    applyAllFilters();
    if (inspectionModeRef.current.enabled) {
      setDefaultMapLayersVisibility(map.current, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showLines, showLine500, showLine220, showLine110, showLine35,
    showSubs, showSub500, showSub220, showSub110, showSub35
  ]);

  // --------- Статистика по активным фильтрам ----------
  const VOLT_COLORS = { "500": "#ff0000", "220": "#1fab15", "110": "#ff9900", "35": "#0000ff" };
  const FILTER_VOLTS = ["500", "220", "110", "35"];

  const filterStats = useMemo(() => {
    // какие напряжения активны
    const activeLineVolts = showLines
      ? FILTER_VOLTS
      : FILTER_VOLTS.filter(v => ({ "500": showLine500, "220": showLine220, "110": showLine110, "35": showLine35 }[v]));
    const activeSubVolts = showSubs
      ? FILTER_VOLTS
      : FILTER_VOLTS.filter(v => ({ "500": showSub500, "220": showSub220, "110": showSub110, "35": showSub35 }[v]));

    // линии: группируем по voltage
    const linesByVolt = {};
    let totalLength = 0;
    let totalLineCount = 0;
    for (const n of networks) {
      const v = String(n.voltage ?? "");
      const primary = pickPrimaryVoltage(extractVoltages(v)) || v;
      if (!activeLineVolts.includes(primary)) continue;
      if (!linesByVolt[primary]) linesByVolt[primary] = { count: 0, length: 0 };
      linesByVolt[primary].count += 1;
      const len = parseFloat(String(n.length ?? "").replace(",", ".")) || 0;
      linesByVolt[primary].length += len;
      totalLength += len;
      totalLineCount += 1;
    }

    // подстанции: группируем по первому напряжению
    const subsByVolt = {};
    let totalSubCount = 0;
    for (const s of substations) {
      const kv = firstVoltageKv(s.voltage);
      const primary = kv != null ? String(kv) : "";
      if (!activeSubVolts.includes(primary)) continue;
      if (!subsByVolt[primary]) subsByVolt[primary] = { count: 0 };
      subsByVolt[primary].count += 1;
      totalSubCount += 1;
    }

    const lines = FILTER_VOLTS
      .filter(v => linesByVolt[v])
      .map(v => ({ voltage: v, ...linesByVolt[v] }));
    const subs = FILTER_VOLTS
      .filter(v => subsByVolt[v])
      .map(v => ({ voltage: v, ...subsByVolt[v] }));

    return { lines, subs, totalLength, totalLineCount, totalSubCount };
  }, [substations, networks, showLines, showLine500, showLine220, showLine110, showLine35,
      showSubs, showSub500, showSub220, showSub110, showSub35]);

  // --------- ДОБАВЛЕНО: хендлеры для чекбоксов ----------
  // ---- Линии
  const toggleShowLines = (checked) => {
    setShowLines(checked);
    if (checked) {
      setShowLine500(false);
      setShowLine220(false);
      setShowLine110(false);
      setShowLine35(false);
    }
  };
  const toggleLine500 = (checked) => { setShowLine500(checked); if (checked) setShowLines(false); };
  const toggleLine220 = (checked) => { setShowLine220(checked); if (checked) setShowLines(false); };
  const toggleLine110 = (checked) => { setShowLine110(checked); if (checked) setShowLines(false); };
  const toggleLine35  = (checked) => { setShowLine35(checked);  if (checked) setShowLines(false); };

  // ---- Подстанции
  const toggleShowSubs = (checked) => {
    setShowSubs(checked);
    if (checked) {
      setShowSub500(false);
      setShowSub220(false);
      setShowSub110(false);
      setShowSub35(false);
    }
  };
  const toggleSub500 = (checked) => { setShowSub500(checked); if (checked) setShowSubs(false); };
  const toggleSub220 = (checked) => { setShowSub220(checked); if (checked) setShowSubs(false); };
  const toggleSub110 = (checked) => { setShowSub110(checked); if (checked) setShowSubs(false); };
  const toggleSub35  = (checked) => { setShowSub35(checked);  if (checked) setShowSubs(false); };

  const shareLocation = (lng, lat) =>
    window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`, "_blank");
  useEffect(() => { window.shareLocation = shareLocation; }, []);

  const openDetails = () => {
    if (!info) return;
    const base = "https://map.uzbekistonmet.uz/indicators";
    let url = null;

    if (info.type === "substation") {
      // slug подстанции
      const slug = info.props?.slug;
      if (slug) {
        url = `${base}/substations/${encodeURIComponent(slug)}`;
      }
    } else if (info.type === "line") {
      // slug линии (сети)
      const slug = info.props?.slug;
      if (slug) {
        url = `${base}/networks/${encodeURIComponent(slug)}`;
      }
    } else if (info.type === "pole") {
      // slug сети + ID опоры
      const networkSlug = info.props?.network_slug;
      const poleId = info.props?.id;
      if (networkSlug && poleId != null) {
        url = `${base}/networks/${encodeURIComponent(networkSlug)}/poles/${encodeURIComponent(String(poleId))}`;
      }
    }

    if (url) {
      window.open(url, "_blank");
    }
  };


  // Рендер
  return (
    <div
      style={{ width: "100%", height: "100vh", position: "relative", background: "#000" }}
      data-inspection-selected-line={selectedInspectionLineId || undefined}
      data-inspection-selected-pole={selectedInspectionPoleId || undefined}
    >
      {/* Кнопка «Назад» — только мобилки */}
      <a href="/" className="map-back-btn" aria-label="Орқага">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
      </a>

      {/* Левая инфо-панель */}
      {inspectionActive && infoOpen && (
        <InspectionInfoPanel
          year={inspectionYear}
          summary={inspectionSummary}
          summaryLoading={inspectionSummaryLoading}
          summaryError={inspectionError}
          selected={selectedInspection}
          viewportLoading={inspectionLoading}
          onClose={() => setInfoOpen(false)}
          onClearSelection={() => {
            clearSelection();
            clearInspectionHighlight();
            setSelectedInspection(null);
          }}
          onShareLocation={shareLocation}
          onPoleSelect={focusInspectionPoleFromList}
        />
      )}

      {!inspectionActive && info && infoOpen && (
        <div className="map-info-panel">
          <div className="map-info-panel__header">
            <div className="map-info-panel__header-left">
              <div className="map-info-panel__title">Умумий маълумотлар</div>
              {/* <div className="map-info-panel__subtitle">
                {info.type === "substation" ? (info.props?.name || "—")
                  : info.type === "line" ? (info.props?.name || "—")
                  : info.type === "pole" ? (`Таянч №: ${info.props?.name || "—"}`)
                  : "—"}
              </div> */}
            </div>

            <button
              className="map-info-panel__close"
              onClick={() => { setInfo(null); setInfoOpen(false); clearSelection(); }}
              aria-label="Close"
              title="Close"
            >
              ×
            </button>
          </div>

          <div className="map-info-panel__body"> 
            {info.type === "line" && (
              <div className="map-info-fields">
                <div className="title"><strong>ЭУҲТ номи:</strong> {info.props?.name || "—"}</div>
                <div><strong>Филиал:</strong> {info.props?.city || "—"}</div>
                <div><strong>Кучланиши:</strong> {info.props?.voltage || "—"}</div>
                <div><strong>Узунлиги:</strong> {info.props?.length || "—"} км</div>
                <div><strong>Ишга тушган йили:</strong> {info.props?.start_year || "—"}</div>
                <div><strong>Участка:</strong> {info.props?.span || "—"}</div>
                <div><strong>Сим тури:</strong> {info.props?.wire_type || "—"}</div>
                <div><strong>Ҳимоя троси:</strong> {info.props?.wire_protection_type || "—"}</div>
                <div><strong>Оптик трос:</strong> {info.props?.optical_wire_type || "—"}</div>
                <div><strong>Таянчлар сони:</strong> {info.props?.poles_quantity || "—"}</div>
                <div><strong>Кесишувлар:</strong> {info.props?.intersections || "—"}</div>
                {/* <div><strong>Авария маълумоти:</strong> {info.props?.accident_info || "—"}</div>
                <div><strong>Сабаб:</strong> {info.props?.accident_reason || "—"}</div>
                <div><strong>Чоралар:</strong> {info.props?.accident_measures || "—"}</div> */}
                {/* <div><strong>Кўрик:</strong> {info.props?.inspection || "—"}</div>
                <div><strong>Муқаммал таъмир:</strong> {info.props?.major_repair || "—"}</div>
                <div><strong>Жорий таъмир:</strong> {info.props?.current_repair || "—"}</div> */}
              </div>
            )}

            {info.type === "substation" && (
              <div className="map-info-fields">
                <div className="title"><strong>Подстанция: {info.props?.name || "—"}</strong></div>

                {info.props?.image && (
                  <img
                    alt="Подстанция"
                    src={`http://localhost:8055/assets/${info.props.image}`}
                    style={{ width: "100%", borderRadius: 6, margin: "6px 0" }}
                  />
                )}

                <div><strong>МЭТ филиали:</strong> {info.props?.branch || "—"}</div>
                <div><strong>ПС жойлашган ҳудуд:</strong> {info.props?.address || "—"}</div>
                <div><strong>Кучланиши:</strong> {info.props?.voltage_raw || info.props?.voltage || "—"}</div>
                <div><strong>Ишга туширилган йили:</strong> {info.props?.start_year || "—"}</div>
                <div><strong>Қуввати:</strong> {info.props?.power_mva || "—"}</div>

                {info.lngLat && (
                  <button className="map-info-btn" onClick={() => shareLocation(info.lngLat.lng, info.lngLat.lat)}>
                    Google Mapsда очиш
                  </button>
                )}

                {/* <button
                  onClick={() => {
                    drawingSubIdRef.current = info.props?.id;
                    drawRef.current?.changeMode("draw_polygon");
                  }}
                  style={{ marginTop: 8 }}
                >
                  Начертить границу подстанции
                </button> */}

                {/* {(() => {
                  const arr = substationAreas.filter(f => f?.properties?.substation_id === info.props?.id);
                  if (!arr.length) return null;
                  const totalM2 = arr.reduce((s, f) => s + (turf.area(f) || 0), 0);
                  return (
                    <div style={{ marginTop: 6, color: "#555" }}>
                      <div><strong>Контуров:</strong> {arr.length}</div>
                      <div><strong>Площадь:</strong> {(totalM2/10000).toFixed(2)} га</div>
                    </div>
                  );
                })()} */}
              </div>
            )}

            {info.type === "pole" && (
              <div className="map-info-fields">
                <div className="title"><strong>Таянч №:</strong> {info.props?.name || "—"}</div>
                <div><strong>Линия:</strong> {info.props?.network || "—"}</div>
                <div><strong>Филиал:</strong> {info.props?.city || "—"}</div>
                <div><strong>Кучланиш:</strong> {info.props?.voltage || "—"}</div>
                <div><strong>Маркаси:</strong> {info.props?.pole_brand || "—"}</div>
                <div><strong>Тури:</strong> {info.props?.pole_type || "—"}</div>
                <div><strong>Сим тури:</strong> {info.props?.wire_type || "—"}</div>
                <div><strong>Чақмоқдан ҳимоя троси:</strong> {info.props?.lightning_wire_type || "—"}</div>
                <div><strong>Оптик трос тури:</strong> {info.props?.optical_wire_type || "—"}</div>
                <div><strong>Изолятор тури:</strong> {info.props?.insulator_type || "—"}</div>
                <div><strong>Илиш-улаш арматураси:</strong> {info.props?.fittings || "—"}</div>
                <div><strong>Қушлардан ҳимоя:</strong> {info.props?.bird_protection || "—"}</div>
                <div><strong>Охирги кўрик/таъмир:</strong> {info.props?.inspection || "—"}</div>

                {info.lngLat && (
                  <button
                    className="map-info-btn"
                    onClick={() => shareLocation(info.lngLat.lng, info.lngLat.lat)}
                  >
                    Google Mapsда очиш
                  </button>
                )}
              </div>
            )}

            {/* Кнопка "Подробнее" для всех трёх типов */}
            {(info.type === "line" || info.type === "substation" || info.type === "pole") && (
              <div className="map-info-panel__actions">
                <button className="map-info-btn map-info-btn--primary" onClick={openDetails}>
                  Батафсил
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Правый верх: Поиск -> Переключатель стиля -> Кнопка фильтров -> (Фильтры) */}
      <button
        className="map-controls-toggle"
        onClick={() => setControlsOpen(v => !v)}
        type="button"
      >
        Управление
      </button>

      {((!inspectionActive && info) || inspectionActive) && isMobile && !infoOpen && (
        <button
          className="map-info-fab"
          type="button"
          onClick={() => setInfoOpen(true)}
        >
          Открыть инфо
        </button>
      )}

      <div className={`map-controls ${controlsOpen ? "is-open" : ""}`}>
        {/* Поиск */}
        <div className="map-controls__card map-search" ref={searchBoxRef}>
          <div className="map-search__box">
            <svg className="map-search__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="10.5" cy="10.5" r="7"/><line x1="16" y1="16" x2="21" y2="21"/></svg>
            <input
              className="map-search__input"
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder={inspectionActive ? "Линия ёки таянч излаш..." : "Излаш..."}
              autoComplete="off"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={clearSearch}
                title={inspectionActive ? "Тозалаш" : "Очистить"}
                className="map-search__clear"
                aria-label={inspectionActive ? "Тозалаш" : "Очистить"}
              >
                ×
              </button>
            )}
          </div>

          {searchResults.length > 0 && (
            <div className="map-search__results">
              {searchResults.map((r, i) => (
                <button
                  key={`${r.type}-${r.id}`}
                  onClick={() => moveToResult(r)}
                  className={`map-search__item ${r.disabled ? "is-disabled" : ""}`}
                  type="button"
                  disabled={!!r.disabled}
                >
                  <div className="map-search__title">{r.title}</div>
                  <div className="map-search__meta">{r.meta}</div>
                </button>
              ))}
            </div>
          )}

          {inspectionActive && searchQuery.trim() && !searchLoading && searchResults.length === 0 && (
            <div className="map-search__empty">Маълумот топилмади</div>
          )}
        </div>

        {/* Переключатель стиля */}
        <div className="map-controls__card map-controls__mode">
          <button
            onClick={() => setBaseStyle("streets")}
            className={`map-controls__style-btn ${baseStyle === "streets" ? "is-active" : ""}`}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="3" x2="9" y2="21"/></svg>
            Схема
          </button>
          <button
            onClick={() => setBaseStyle("satellite")}
            className={`map-controls__style-btn ${baseStyle === "satellite" ? "is-active" : ""}`}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><ellipse cx="12" cy="12" rx="4" ry="10"/></svg>
            Спутник
          </button>
          <button
            onClick={() => setBaseStyle("google")}
            className={`map-controls__style-btn ${baseStyle === "google" ? "is-active" : ""}`}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>
            Google
          </button>
        </div>

        {/* Кнопка показа/скрытия фильтров */}
        <InspectionModeControls
          mode={mapMode}
          canInspectionMode={canInspectionMode}
          year={inspectionYear}
          onYearChange={(year) => {
            if (Number.isInteger(year)) setInspectionYear(year);
          }}
          onlyUninspected={onlyUninspected}
          onOnlyUninspectedChange={setOnlyUninspected}
          onModeChange={setMapMode}
          error={inspectionError}
          truncated={inspectionTruncated}
        />

        {!inspectionActive && <button
          onClick={() => setFiltersOpen(v => !v)}
          className={`map-controls__filter-toggle ${filtersOpen ? "is-open" : ""}`}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="4" y1="6" x2="20" y2="6"/><line x1="7" y1="12" x2="17" y2="12"/><line x1="10" y1="18" x2="14" y2="18"/></svg>
          {filtersOpen ? "Фильтрларни ёпиш" : "Фильтрлар"}
          <svg className="map-controls__chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
        </button>}

        {/* Фильтры */}
        {!inspectionActive && filtersOpen && (
          <div className="map-controls__filters">
            <div className="map-filters__section">
              <div className="map-filters__label">ЭУХТ</div>
              <div className="map-filters__pills">
                <button
                  className={`map-filters__pill map-filters__pill--all ${showLines ? "is-active" : ""}`}
                  onClick={() => toggleShowLines(!showLines)}
                >Барчаси</button>
                {FILTER_VOLTS.map(v => {
                  const checked = { "500": showLine500, "220": showLine220, "110": showLine110, "35": showLine35 }[v];
                  const toggle = { "500": toggleLine500, "220": toggleLine220, "110": toggleLine110, "35": toggleLine35 }[v];
                  return (
                    <button
                      key={`line-${v}`}
                      className={`map-filters__pill ${checked ? "is-active" : ""}`}
                      style={checked ? { "--pill-color": VOLT_COLORS[v] } : {}}
                      onClick={() => toggle(!checked)}
                    >
                      <span className="map-filters__dot" style={{ background: VOLT_COLORS[v] }} />
                      {v} кВ
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="map-filters__divider" />

            <div className="map-filters__section">
              <div className="map-filters__label">Подстанция</div>
              <div className="map-filters__pills">
                <button
                  className={`map-filters__pill map-filters__pill--all ${showSubs ? "is-active" : ""}`}
                  onClick={() => toggleShowSubs(!showSubs)}
                >Барчаси</button>
                {FILTER_VOLTS.map(v => {
                  const checked = { "500": showSub500, "220": showSub220, "110": showSub110, "35": showSub35 }[v];
                  const toggle = { "500": toggleSub500, "220": toggleSub220, "110": toggleSub110, "35": toggleSub35 }[v];
                  return (
                    <button
                      key={`sub-${v}`}
                      className={`map-filters__pill ${checked ? "is-active" : ""}`}
                      style={checked ? { "--pill-color": VOLT_COLORS[v] } : {}}
                      onClick={() => toggle(!checked)}
                    >
                      <span className="map-filters__dot" style={{ background: VOLT_COLORS[v] }} />
                      {v} кВ
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Инфо-окно статистики */}
        {!inspectionActive && <div className="map-controls__stats">
          <div className="map-stats__section">
            <div className="map-stats__title">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>
              ЭУХТ
            </div>
            {filterStats.lines.length > 0 ? filterStats.lines.map(l => (
              <div key={l.voltage} className="map-stats__item">
                <span className="map-stats__dot" style={{ background: VOLT_COLORS[l.voltage] }} />
                <span className="map-stats__voltage">{l.voltage} кВ</span>
                <span className="map-stats__value">{l.count} та · {l.length.toFixed(1)} км</span>
              </div>
            )) : (
              <div className="map-stats__empty">—</div>
            )}
            {filterStats.lines.length > 1 && (
              <div className="map-stats__total">
                <span>Жами: {filterStats.totalLineCount} та</span>
                <span>{filterStats.totalLength.toFixed(1)} км</span>
              </div>
            )}
          </div>
          <div className="map-stats__section">
            <div className="map-stats__title">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="3"/><path d="M12 2v4m0 12v4m-10-10h4m12 0h4" strokeLinecap="round"/></svg>
              ПС
            </div>
            {filterStats.subs.length > 0 ? filterStats.subs.map(s => (
              <div key={s.voltage} className="map-stats__item">
                <span className="map-stats__dot" style={{ background: VOLT_COLORS[s.voltage] }} />
                <span className="map-stats__voltage">{s.voltage} кВ</span>
                <span className="map-stats__value">{s.count} та</span>
              </div>
            )) : (
              <div className="map-stats__empty">—</div>
            )}
            {filterStats.subs.length > 1 && (
              <div className="map-stats__total">
                <span>Жами: {filterStats.totalSubCount} та</span>
              </div>
            )}
          </div>
        </div>}

        {!inspectionActive && canMapCreate && (
          <button
            type="button"
            className="map-controls__add-btn"
            onClick={openPolesEditor}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Таянч қўшиш
          </button>
        )}
      </div>

      <VideoModal
        open={videoOpen}
        onClose={() => setVideoOpen(false)}
        title="Таянч қўшиш — инструкция"
        videoId={VIDEO_IDS.addPole}
      />

      {polesEditorOpen && (
        <div className={`map-info-panel ${mobileEditorHidden ? "map-info-panel--mobile-hidden" : ""}`}>
          <div className="map-info-panel__header">
            <div className="map-info-panel__header-left">
              <div className="map-info-panel__title">Таянч қўшиш</div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button
                className="map-video-btn"
                onClick={() => setVideoOpen(true)}
                title="Видео инструкция"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <polygon points="10 8 16 12 10 16 10 8" fill="currentColor" stroke="none" />
                </svg>
                Инструкция
              </button>
              <button
                className="map-info-panel__close"
                onClick={closePolesEditor}
                aria-label="Close"
                title="Close"
              >
                ×
              </button>
            </div>
          </div>

          <div className="map-info-panel__body">
            {/* Поиск линии */}
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontWeight: 700, marginBottom: 6, color: "rgba(255,255,255,0.7)" }}>Линияни қидириш</div>
              <input
                value={draftSearch}
                onChange={(e) => setDraftSearch(e.target.value)}
                placeholder="Линия номини киритинг"
                className="pole-input"
              />

              {draftSearch.trim() && networkSearchResults.length > 0 && (
                <div style={{ marginTop: 8, border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, overflow: "hidden" }}>
                  {networkSearchResults.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => selectDraftNetwork(r.id)}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        padding: 10,
                        border: "none",
                        borderBottom: "1px solid rgba(255,255,255,0.06)",
                        background: "rgba(255,255,255,0.04)",
                        color: "rgba(255,255,255,0.9)",
                        cursor: "pointer",
                      }}
                    >
                      {r.title}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Выбранная линия */}
            <div style={{ padding: 10, borderRadius: 10, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.06)", marginBottom: 5, color: "rgba(255,255,255,0.8)" }}>
              <div>Линия: <b style={{ color: "#fff" }}>{draftNetworkTitle || "—"}</b></div>
              {/* <div>Филиал: <b>{draftCityTitle || "—"}</b></div> */}
            </div>

            {draftError && (
              <div style={{ marginTop: 8, padding: 10, borderRadius: 10, background: "rgba(255,60,60,0.12)", border: "1px solid rgba(255,60,60,0.2)", color: "#ff6b6b" }}>
                {draftError}
              </div>
            )}

            {/* Добавление точек */}
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <button
                type="button"
                className="map-info-btn map-info-btn--primary"
                onClick={() => {
                  if (!draftNetworkId) {
                    setDraftError("Сначала выбери линию.");
                    return;
                  }
                  setDraftError("");
                  setPickPoleMode(true);
                  // на мобилке скрываем панель чтобы карта была доступна
                  if (window.innerWidth <= 768) setMobileEditorHidden(true);
                }}
              >
                Таянч қўшиш
              </button>

              {/* <button
                type="button"
                className="map-info-btn"
                onClick={() => {
                  setDraftPoles([]);
                  setPickPoleMode(false);
                  setDraftError("");
                }}
              >
                Очистить
              </button> */}
              <button
                type="button"
                className="map-info-btn"
                onClick={() => setConfirmClearOpen(true)}
                disabled={!draftPoles.length}
              >
                Тозалаш
              </button>
              <button
                type="button"
                className="map-info-btn"
                onClick={handleGeneratePoleNumbers}
                disabled={!draftPoles.length}
              >
                Рақамларни автоматик бериш
              </button>
            </div>

            {confirmClearOpen && (
              <div style={{ marginTop: 10, padding: 10, borderRadius: 10, background: "rgba(255,180,0,0.1)", border: "1px solid rgba(255,180,0,0.2)", color: "#ffcc57" }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>Ўчиришни тасдиқлайсизми?</div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    type="button"
                    className="map-info-btn map-info-btn--primary"
                    onClick={() => {
                      setDraftPoles([]);
                      setPickPoleMode(false);
                      setDraftError("");
                      setConfirmClearOpen(false);
                    }}
                  >
                    Ҳа
                  </button>

                  <button
                    type="button"
                    className="map-info-btn"
                    onClick={() => setConfirmClearOpen(false)}
                  >
                    Йўқ
                  </button>
                </div>
              </div>
            )}

            {pickPoleMode && (
              <div style={{ marginTop: 10, padding: 10, borderRadius: 10, background: "rgba(79,156,247,0.1)", border: "1px solid rgba(79,156,247,0.2)", color: "#7bb8ff" }}>
                Режим: таянч нуқтасини қўйиш учун харитада босинг…
              </div>
            )}

            {/* Черновик */}
            <div style={{ marginTop: 14 }}>
              <div style={{ fontWeight: 700, marginBottom: 8, color: "rgba(255,255,255,0.7)" }}>Қоралама ({draftPoles.length})</div>

              {draftPoles.length === 0 ? (
                <div style={{ color: "rgba(255,255,255,0.35)", fontSize: 13 }}>
                  “Таянч қўшиш”ни босинг ва харитада босинг.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {draftPoles.map((p, idx) => (
                    <div key={p.tmpId} className="map-pole-card">
                      <div className="map-pole-card__top">
                        <div className="map-pole-card__idx">#{idx + 1}</div>

                        <button
                          type="button"
                          className="map-pole-card__remove"
                          onClick={() => setDraftPoles((prev) => prev.filter((x) => x.tmpId !== p.tmpId))}
                        >
                          Ўчириш
                        </button>
                      </div>

                      {/* Всегда видимое поле */}
                      <input
                        className="pole-input"
                        value={p.name}
                        onChange={(e) => {
                          const v = e.target.value;
                          setDraftPoles((prev) => prev.map((x) => (x.tmpId === p.tmpId ? { ...x, name: v } : x)));
                        }}
                        placeholder="Имя/номер опоры (обязательно)"
                      />

                      <div className="pole-coords">
                        <div style={{ display: "flex", gap: 8 }}>
                          <div style={{ flex: 1 }}>
                            <label style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginBottom: 2, display: "block" }}>Широта</label>
                            <input
                              className="pole-input"
                              value={p.latStr ?? String(p.latitude ?? "")}
                              onChange={(e) => {
                                const raw = e.target.value;
                                const num = parseCoordinate(raw, "lat");
                                setDraftPoles((prev) =>
                                  prev.map((x) =>
                                    x.tmpId === p.tmpId
                                      ? { ...x, latStr: raw, latitude: num }
                                      : x
                                  )
                                );
                              }}
                              placeholder="Кенглик"
                            />
                          </div>
                          <div style={{ flex: 1 }}>
                            <label style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginBottom: 2, display: "block" }}>Долгота</label>
                            <input
                              className="pole-input"
                              value={p.lngStr ?? String(p.longitude ?? "")}
                              onChange={(e) => {
                                const raw = e.target.value;
                                const num = parseCoordinate(raw, "lng");
                                setDraftPoles((prev) =>
                                  prev.map((x) =>
                                    x.tmpId === p.tmpId
                                      ? { ...x, lngStr: raw, longitude: num }
                                      : x
                                  )
                                );
                              }}
                              placeholder="Узунлик"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Скрытый блок */}
                      <details className="pole-details">
                        <summary className="pole-details__summary">Қўшимча</summary>

                        <div className="pole-grid">
                          <div className="pole-field">
                            <label>Марка опоры</label>
                            <input
                              value={p.pole_brand}
                              onChange={(e) => {
                                const v = e.target.value;
                                setDraftPoles((prev) => prev.map((x) => (x.tmpId === p.tmpId ? { ...x, pole_brand: v } : x)));
                              }}
                            />
                          </div>

                          <div className="pole-field">
                            <label>Тип опоры</label>
                            <input
                              value={p.pole_type}
                              onChange={(e) => {
                                const v = e.target.value;
                                setDraftPoles((prev) => prev.map((x) => (x.tmpId === p.tmpId ? { ...x, pole_type: v } : x)));
                              }}
                            />
                          </div>

                          <div className="pole-field">
                            <label>Тип провода</label>
                            <input
                              value={p.wire_type}
                              onChange={(e) => {
                                const v = e.target.value;
                                setDraftPoles((prev) => prev.map((x) => (x.tmpId === p.tmpId ? { ...x, wire_type: v } : x)));
                              }}
                            />
                          </div>

                          <div className="pole-field">
                            <label>Грозотрос</label>
                            <input
                              value={p.lightning_wire_type}
                              onChange={(e) => {
                                const v = e.target.value;
                                setDraftPoles((prev) => prev.map((x) => (x.tmpId === p.tmpId ? { ...x, lightning_wire_type: v } : x)));
                              }}
                            />
                          </div>

                          <div className="pole-field">
                            <label>Оптик трос</label>
                            <input
                              value={p.optical_wire_type}
                              onChange={(e) => {
                                const v = e.target.value;
                                setDraftPoles((prev) => prev.map((x) => (x.tmpId === p.tmpId ? { ...x, optical_wire_type: v } : x)));
                              }}
                            />
                          </div>

                          <div className="pole-field pole-field--full">
                            <label>Изолятор</label>
                            <textarea
                              rows={2}
                              value={p.insulator_type}
                              onChange={(e) => {
                                const v = e.target.value;
                                setDraftPoles((prev) => prev.map((x) => (x.tmpId === p.tmpId ? { ...x, insulator_type: v } : x)));
                              }}
                            />
                          </div>

                          <div className="pole-field pole-field--full">
                            <label>Арматура</label>
                            <textarea
                              rows={2}
                              value={p.fittings}
                              onChange={(e) => {
                                const v = e.target.value;
                                setDraftPoles((prev) => prev.map((x) => (x.tmpId === p.tmpId ? { ...x, fittings: v } : x)));
                              }}
                            />
                          </div>

                          <div className="pole-field pole-field--full">
                            <label>Птицезащита</label>
                            <textarea
                              rows={2}
                              value={p.bird_protection}
                              onChange={(e) => {
                                const v = e.target.value;
                                setDraftPoles((prev) => prev.map((x) => (x.tmpId === p.tmpId ? { ...x, bird_protection: v } : x)));
                              }}
                            />
                          </div>

                          {/* <div className="pole-field pole-field--full">
                            <label>Кўрик / Таъмир</label>
                            <textarea
                              rows={2}
                              value={p.inspection}
                              onChange={(e) => {
                                const v = e.target.value;
                                setDraftPoles((prev) => prev.map((x) => (x.tmpId === p.tmpId ? { ...x, inspection: v } : x)));
                              }}
                            />
                          </div> */}
                        </div>
                      </details>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Кнопки */}
            <div style={{ marginTop: 14, display: "flex", gap: 8 }}>
              <button
                type="button"
                className="map-info-btn map-info-btn--primary"
                onClick={handleSaveDraftPoles}
                disabled={!canSaveDraft}
              >
                Сақлаш
              </button>
              {/* <button type="button" className="map-info-btn" onClick={closePolesEditor}>
                Закрыть
              </button> */}
            </div>
          </div>
        </div>
      )}
      {/* Контейнер для карты */}
      <div ref={mapContainer} style={{ width: "100%", height: "100%" }} />
    </div>
  );
};

export default CustomMap;
